// The "AI costs" tab on a company's page.
//
// ★ WHAT OMAR ASKED FOR, 2026-09-08 (verbatim):
//
//   "Implement the company overview and the individual course view inside the
//    same tab. Keep the selected reporting period when moving between them, with
//    a clear 'All courses' control to return.
//
//    Both views must display the same five categories, in this order:
//      · Question and text generation · Subtitle generation · Image generation
//      · Video generation · Translation
//
//    Video should show 'Not available'; this does not add video generation to
//    the development scope.
//
//    Make the difference between 'Nothing recorded', 'Not yet priced', and
//    'Not available' clear. Missing prices must never appear as zero.
//
//    Include a reporting period, category subtotals, an overall total, and
//    relevant usage information."
//
// Built against a written design specification (a UI design director's, at his
// request); the copy handed to him is `~/Downloads/AI-COSTS-DESIGN-SPEC-2026-09-08.md`.
// Where this file departs from that document, the departure is commented where
// it happens.
//
// ── THE ONE STRUCTURAL IDEA ─────────────────────────────────────────────────
//
// The five categories are a FIXED, ORDERED list owned by `@dynamo/schema`, and
// the API's groups are JOINED ONTO it — never rendered AS it. That single choice
// buys four things at once: the order never varies between the company view and
// a course view, a category with no data still appears (and can therefore say
// "Nothing recorded", which is a finding), Video has a home despite having no
// data and never getting a bucket, and `Other AI usage` stays reachable so an
// operation nobody has classified yet cannot fall out of a total that still
// calls itself the total.
//
// ── ⚠️ ONE THING ON THIS SCREEN IS STILL AN OPEN DECISION ───────────────────
//
// Omar, 2026-09-08: "The proposed relabelling of calculated costs also remains
// unresolved; removing the word 'estimated' does not establish that a figure is
// an actual charge." The design specification proposed replacing "estimated"
// with "recorded" throughout. That is deliberately NOT done here. Every money
// label still says "estimated", and all of those words live in `MONEY_WORDS`
// below, so settling the question later is one edit in one place rather than a
// hunt through JSX.

import * as React from "react";
import {
  AI_COST_CATEGORIES,
  type AiCostCategory,
  internalAttributionLabel,
  OTHER_AI_COST_CATEGORY,
} from "@dynamo/schema";

import type {
  AiAttributionGap,
  AiAttributionGaps,
  AiCostGroup,
  ChargedAmount,
  AiCostReport,
  AiCostTotals,
  AiProviderCostRow,
  ConsoleApi,
  CostImportProviderStatus,
  CostImportStatus,
} from "./api.js";
import { ErrorState, Loading, fmtDate, fmtDateTime, useAsync } from "./ui.js";

/**
 * ⚠️ AWAITING OMAR'S DECISION — see the header. Every user-visible word about
 * money is here and nowhere else, so the answer costs one edit.
 */
const MONEY_WORDS = {
  companyHeading: "CHARGED BY PROVIDERS",
  courseHeading: "THIS COURSE'S SHARE",
  column: "Charged",
  courseColumn: "Share of charge",
  provenance:
    "Every figure here is imported from a provider's own billing report — not " +
    "calculated from a price list. The headline figure is the USAGE COST, " +
    "before any promotional credit; where a credit was applied, the credit and " +
    "the net payable are shown beneath it. A company's figure is what the " +
    "provider charged. A course's figure is that company charge divided by what " +
    "each course measurably used, because no provider reports per course — it " +
    "is Dynamo's calculated allocation and not a provider's own number.",
} as const;

/**
 * The key the report uses for work that is not tied to a course.
 *
 * Matched on the KEY, never on the label: the label is human text somebody will
 * reword one day, and matching on it would silently reclassify the row
 * (`feedback_a_readable_label_can_name_the_wrong_thing`).
 */
const UNATTRIBUTED = "__unattributed__";

/**
 * The day the usage ledger started recording.
 *
 * Shown beside any window reaching further back, so "Last 12 months" cannot
 * quietly imply twelve months of data. Nothing before this was ever stored, and
 * Omar has already ruled it is not to be reconstructed: "I am not interested in
 * reconstructing historical costs" (2026-09-07).
 */
const LEDGER_START = new Date("2026-09-07T00:00:00.000Z");

// ── The reporting period ────────────────────────────────────────────────────

/**
 * ★ CALENDAR MONTHS AND ROLLING WINDOWS ARE BOTH HERE, deliberately. "How much
 * this month" is asked against an invoice; "the last 30 days" is asked about a
 * trend. They are different questions and neither can be read off the other.
 */
const PERIODS = [
  { id: "month", label: "This month" },
  { id: "lastMonth", label: "Last month" },
  { id: "30", label: "Last 30 days" },
  { id: "90", label: "Last 90 days" },
  { id: "365", label: "Last 12 months" },
  { id: "all", label: "All time" },
] as const;

type PeriodId = (typeof PERIODS)[number]["id"];

export function windowFor(
  id: PeriodId,
  now: Date = new Date(),
): { from: string; to?: string } {
  const days = (n: number) =>
    new Date(now.getTime() - n * 86_400_000).toISOString();
  const monthStart = (offset: number) =>
    new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
  switch (id) {
    case "month":
      return { from: monthStart(0).toISOString() };
    case "lastMonth":
      // A CLOSED window — both ends given, so it cannot creep as the day moves.
      return {
        from: monthStart(-1).toISOString(),
        to: monthStart(0).toISOString(),
      };
    case "30":
      return { from: days(30) };
    case "90":
      return { from: days(90) };
    case "365":
      return { from: days(365) };
    case "all":
    default:
      // Far enough back to predate the ledger itself — NOT `undefined`, which
      // would silently take the gateway's 30-day default and then label itself
      // "All time".
      return { from: new Date("2020-01-01T00:00:00.000Z").toISOString() };
  }
}

// ── Numbers ─────────────────────────────────────────────────────────────────

function count(n: number): string {
  return n.toLocaleString("en-GB");
}

/**
 * How many decimal places a column of money needs.
 *
 * ★ OMAR'S INSTRUCTION, 2026-09-08: "increase precision when necessary to show
 * small non-zero amounts. Use '< $0.0001' only when the actual amount is below
 * that threshold." So the precision is chosen from the SMALLEST non-zero value
 * in the column rather than fixed, and it is uniform within the column so the
 * decimal points still line up under `tabular-nums`.
 *
 * The failure it replaces: two fixed decimal places rendered a real cost of
 * $0.0068 as "$0.00" — the same lie as showing $0.00 for an unpriced category,
 * arriving by a different route.
 */
export function precisionFor(values: readonly number[]): number {
  const nonZero = values.filter((v) => v > 0);
  if (nonZero.length === 0) return 2;
  const smallest = Math.min(...nonZero);
  if (smallest >= 0.01) return 2;
  if (smallest >= 0.0001) return 4;
  return 6;
}

/**
 * Money, in the currency it was calculated in.
 *
 * ★ USD, and it says USD. The providers' list prices are dollar prices and the
 * ledger stores dollars; rendering them with a € because Omar's invoice is in
 * euros would apply an exchange rate nobody chose
 * (`feedback_a_coordinate_is_not_defined_until_order_unit_and_frame_are`).
 */
export function money(n: number, dp: number): string {
  const smallest = Math.pow(10, -dp);
  // A non-zero amount must never print as zero. Below what the column can show
  // it says so — and says the TRUE threshold for this column, not a fixed one.
  if (n > 0 && n < smallest / 2) return `< $${smallest.toFixed(dp)}`;
  return `$${n.toFixed(dp)}`;
}

/** `audio_seconds` → `3 h 12 m of audio`. The same quantity, in a readable unit. */
export function humanUnit(unit: string, amount: number): string {
  if (unit === "audio_seconds") {
    if (amount < 60) return `${count(Math.round(amount))} seconds of audio`;
    if (amount < 3600)
      return `${count(Math.round(amount / 60))} minutes of audio`;
    const h = Math.floor(amount / 3600);
    const m = Math.round((amount % 3600) / 60);
    return `${h} h ${m} m of audio`;
  }
  return `${count(amount)} ${unit.replace(/_/g, " ")}`;
}

// ── The five categories, joined onto whatever the API returned ──────────────

type CategoryState = "priced" | "unpriced" | "unavailable" | "empty";

interface CategoryRow {
  category: AiCostCategory | typeof OTHER_AI_COST_CATEGORY;
  group: AiCostGroup | null;
  state: CategoryState;
  /**
   * What the provider charged for this category — its own figure, not ours.
   * Empty when nothing has been imported for it yet, which is a different thing
   * from a provider that can never report it (see `chargeState`).
   */
  charged: ChargedAmount[];
  chargeState: ChargeState;
  /**
   * Work recorded in this category that the provider has NOT yet reported on.
   *
   * ── Omar, 2026-09-16 ──────────────────────────────────────────────────────
   *
   *   "Until Google publishes the new billing data, keep that operation
   *    pending; an existing zero balance does not confirm it has been reported."
   *
   * ★ SEPARATE FROM `chargeState`, deliberately. A category can hold a real
   *   figure AND be waiting on newer usage at the same time, and collapsing the
   *   two into one state forces a choice between showing the money and telling
   *   the truth about what it covers.
   */
  pending: boolean;
  /** How far the provider HAS reported, exclusive. Null when not at all. */
  reportedThrough: string | null;
  /**
   * Why this category's provider has sent nothing — `null` when it has, or when
   * the category has no provider.
   *
   * ★ CARRIED ONTO THE ROW BY THE 2026-09-16 MERGE. The table that used to hold
   *   this was per-provider; folding it away without moving this would have
   *   turned "the credential was revoked" and "the provider has not published
   *   yet" into one sentence — and only one of them needs somebody to act. The
   *   reason a figure is missing was the whole point of that table
   *   (`feedback_absence_and_emptiness_read_the_same`).
   */
  providerState: ProviderCostState | null;
  /**
   * The import's own status for this row's provider — carried so the row can
   * repeat THE PROVIDER'S OWN WORDS when something is wrong.
   *
   * ⚠️ A 403 from Deepgram names the missing scope exactly; paraphrasing it
   *    sends whoever reads this to the wrong switch in a dashboard they do not
   *    know (`feedback_a_clipped_message_turns_an_answer_into_a_fault`). This is
   *    the half of the merged-away table that was worth most and was easiest to
   *    lose.
   */
  providerStatus: CostImportProviderStatus | null;
  /**
   * The import's own status could not be READ — as opposed to reading it and
   * finding a fault. The screen must then claim nothing at all rather than fall
   * back to the reassuring sentence (`feedback_honest_gates_over_standins`).
   */
  statusUnreadable: boolean;
}

/**
 * ★ FOUR DIFFERENT SENTENCES, and collapsing any two of them would be a lie:
 *
 * · `charged`       — the provider billed this, and here is the figure.
 * · `empty`         — nothing was used, so nothing was billed. A fact about the
 *                     RECORD, and it outranks every explanation below it.
 * · `awaiting`      — the provider CAN report it and has not yet. It will arrive.
 * · `not_per_client`— the provider can NEVER report it for one client. Google
 *                     bills every client on one shared project. Showing
 *                     "awaiting" here would promise something that never comes.
 * · `not_built`     — the service does not exist, so nothing was ever charged.
 *
 * ⚠️ A sixth state, `combined`, existed for a few hours on 2026-09-18 and was
 *    REMOVED with the category that needed it. It said "this money is real and
 *    is reported inside another row" — true of a separate Course generation row,
 *    and meaningless now that OpenAI's whole spend is ONE row. Recorded here
 *    rather than deleted silently, because the reasoning is what would have to
 *    be rebuilt if a provider ever does bill two of these categories together.
 */
type ChargeState =
  | "charged"
  | "awaiting"
  | "empty"
  | "not_per_client"
  | "not_built";

function stateOf(
  category: AiCostCategory | typeof OTHER_AI_COST_CATEGORY,
  group: AiCostGroup | null,
  charged: readonly ChargedAmount[] = [],
): CategoryState {
  // A service that does not exist is not a service that spent nothing.
  if (category.availability === "not_built") return "unavailable";
  if (!group || group.requests === 0) return "empty";
  // ── ★ A PROVIDER'S OWN FIGURE BEATS THE ABSENCE OF OUR ESTIMATE ──────────
  //
  // Omar, 2026-09-16: "Fix 'Not yet priced' when a provider-reported amount or
  // a course share derived from it is already available."
  //
  // This badge used to describe ONE thing — whether we had computed a list-price
  // estimate — while the money column beside it described a DIFFERENT thing —
  // whether the provider had billed us. For Deepgram those two disagree by
  // design: no rate is hard-coded for transcription, so the estimate is
  // permanently absent, and the real invoice arrives daily and is attributed per
  // client. The row therefore read "Not yet priced · $0.00996", which is the
  // most solidly evidenced number on the page wearing a label that calls it
  // provisional.
  //
  // ⚠️ `charged` is the SHARE derived from the provider's total, which is what
  //    the row actually displays. Checking the report's own
  //    `estimatedCostUsd` instead would leave the badge disagreeing with the
  //    figure printed next to it — the original defect, moved
  //    (`feedback_a_check_can_match_the_prose_instead_of_the_value`).
  if (charged.length > 0) return "priced";
  // ★ THE ORDER OF THESE TWO LINES IS THE POINT. "Not yet priced" against zero
  //   usage would imply unpriced spend is hiding somewhere. It is not — that
  //   case is `empty`, and it is decided first.
  if (group.estimatedCostUsd === 0 && group.requestsWithoutCost > 0)
    return "unpriced";
  return "priced";
}

/**
 * Which of the four sentences the money column should say.
 *
 * ★ `costReporting` comes from the category itself, so this asks a question
 * about the PROVIDER — "could a figure ever arrive?" — before asking one about
 * our data — "has it?". Asked the other way round, an image-generation row with
 * no figure would read "awaiting provider cost" for ever
 * (`feedback_absence_and_emptiness_read_the_same`).
 */
function chargeStateOf(
  category: AiCostCategory | typeof OTHER_AI_COST_CATEGORY,
  group: AiCostGroup | null,
  charged: readonly ChargedAmount[],
): ChargeState {
  if (category.costReporting === "not_built") return "not_built";
  if (charged.length > 0) return "charged";
  // ★ NOTHING RECORDED BEATS EVERY EXPLANATION OF WHY THERE IS NO FIGURE.
  //   Caught by an existing test on 2026-09-11: a course that never used
  //   translation was being told translation "is not billed per client", which
  //   is true of the provider and beside the point here — nothing was used, so
  //   there is nothing to bill and no explanation is owed. Answering the harder
  //   question first made the simple one unreachable.
  if (!group || group.requests === 0) return "empty";
  if (category.costReporting === "not_per_client") return "not_per_client";
  return "awaiting";
}

/**
 * ★ THE JOIN. The category list leads; the data follows.
 *
 * Iterating the API's groups instead would give a list whose length, order and
 * membership changed with the data — no Video row, no "Nothing recorded" row,
 * and a different shape for every course.
 */
export function categoryRows(
  groups: readonly AiCostGroup[],
  charges: readonly { key: string; charged: ChargedAmount[] }[] = [],
  /**
   * Per category, how far the providers have reported. ⚠️ DEFAULTS TO EMPTY,
   * which reproduces the old behaviour exactly — an older gateway sends no
   * coverage, and this screen must then simply not claim anything is pending
   * rather than claim everything is.
   */
  coverage: readonly {
    key: string;
    reportedThrough: string | null;
    pending: boolean;
  }[] = [],
  /**
   * Asked per provider key when a category has no figure: is the silence
   * normal, or is something broken? ⚠️ OPTIONAL, and its absence means "we
   * cannot say" rather than "nothing is wrong" — so the row falls back to
   * "awaiting", which is what it said before this parameter existed.
   */
  providerHealth?: (provider: string) => ProviderCostState | null,
  /** The import's own status row for a provider, for its verbatim message. */
  providerStatusOf?: (provider: string) => CostImportProviderStatus | null,
  /** True when the status ROUTE failed, which is not the same as a fault. */
  statusUnreadable = false,
): CategoryRow[] {
  const byKey = new Map(groups.map((g) => [g.key, g]));
  const chargeByKey = new Map(charges.map((c) => [c.key, c.charged]));
  const coverByKey = new Map(coverage.map((c) => [c.key, c]));

  const build = (
    category: AiCostCategory | typeof OTHER_AI_COST_CATEGORY,
    group: AiCostGroup | null,
  ): CategoryRow => {
    const charged = chargeByKey.get(category.key) ?? [];
    const cover = coverByKey.get(category.key) ?? null;
    return {
      category,
      group,
      // ★ `charged` is computed ABOVE and passed in, so the badge and the money
      //   column cannot disagree: they are now two readings of one value.
      state: stateOf(category, group, charged),
      charged,
      chargeState: chargeStateOf(category, group, charged),
      pending: cover?.pending ?? false,
      reportedThrough: cover?.reportedThrough ?? null,
      // ★ COMPUTED EVEN WHEN A FIGURE HAS ARRIVED. `providerCostState` ranks an
      //   amount above every explanation, so this reads "reported" — and
      //   `ProviderStatusLine` is then silent unless the most recent refresh
      //   FAILED, which is a figure on screen that can no longer be updated.
      //   Gating it on `charged.length === 0` hid exactly that case.
      providerState:
        "billedBy" in category && category.billedBy
          ? (providerHealth?.(category.billedBy.key) ?? null)
          : null,
      providerStatus:
        "billedBy" in category && category.billedBy
          ? (providerStatusOf?.(category.billedBy.key) ?? null)
          : null,
      statusUnreadable,
    };
  };

  const rows: CategoryRow[] = AI_COST_CATEGORIES.map((category) =>
    build(category, byKey.get(category.key) ?? null),
  );

  // ★ `Other AI usage` is appended only when something landed in it — and it
  //   MUST be appended when it did. Dropping it would leave the five rows
  //   disagreeing with the total beneath them, and the total is the honest one.
  const other = byKey.get("other");
  if (other && other.requests > 0) {
    rows.push(build(OTHER_AI_COST_CATEGORY, other));
  }
  return rows;
}

// ── The section ─────────────────────────────────────────────────────────────

export function AiCostsSection({ api, id }: { api: ConsoleApi; id: string }) {
  const [period, setPeriod] = React.useState<PeriodId>("30");
  // ★ Which course is open lives HERE, beside the period, so returning to the
  //   overview cannot reset the window — Omar: "Keep the selected reporting
  //   period when moving between them."
  const [openCourse, setOpenCourse] = React.useState<string | null>(null);

  // ★ REFRESHED WHILE THE PAGE IS OPEN. Omar, 2026-09-16: "Refresh the Console
  //   figures automatically while the page is open, without requiring a reload
  //   or reporting-period change."
  //
  //   30 seconds is chosen against the thing being waited for: the expedited
  //   import's first attempt is 90 s after a transcription, so the figure can
  //   never sit unseen for more than about half a minute once it lands. Polling
  //   faster would not make it arrive sooner.
  const { state, refreshedAt, retry } = useAsync<AiCostReport>(
    () => {
      const w = windowFor(period);
      return api.aiCosts(id, w.from, w.to);
    },
    [id, period],
    { refreshMs: 30_000 },
  );

  // ★ A SECOND, INDEPENDENT REQUEST, and its failure must not blank this tab.
  //
  // It answers a different question from the one above — "is importing working
  // at all", which is platform-wide and has no period — and the two are kept
  // apart so that a status route that is down still leaves the company's own
  // recorded usage on screen. What it must NOT do is let the screen claim
  // "Awaiting provider cost" when it cannot actually tell: that word promises a
  // figure is coming. When the status is unreadable the rows say so instead
  // (`feedback_honest_gates_over_standins`).
  const importStatus = useAsync<CostImportStatus>(
    () => api.aiCostImportStatus(),
    [id],
  );

  // Narrowing the period until the open course has nothing left is fine — the
  // course view says so. But a period change must never leave open a course the
  // new data has no row for at all.
  React.useEffect(() => {
    if (state.t !== "ok" || openCourse === null) return;
    if (!state.data.byCourse.some((c) => c.key === openCourse))
      setOpenCourse(null);
  }, [state, openCourse]);

  // ★ Built HERE because this is the only scope holding both the api client and
  //   the customer id. On success the report is re-read, so the note that
  //   prompted the action disappears by reflecting the new state rather than by
  //   being hidden locally — a local flag would survive a failure that the
  //   server did not actually accept
  //   (`feedback_a_failure_state_must_read_back_not_infer`).
  // ★★ THE RESULT IS HELD HERE, NOT IN THE BUTTON, AND THAT IS THE POINT.
  //
  //   On success the report is re-read so the screen reflects reality — at which
  //   point the setup note disappears, because the company now HAS a project.
  //   If the outcome lived inside the note it would be destroyed by exactly the
  //   refresh that proves it worked, and the operator would see a flash and
  //   nothing else.
  //
  //   ★ That matters most in the case worth warning about: an ADOPTED project
  //     means two environments now share one bill, and a message that vanishes
  //     is the same as no message at all
  //     (`feedback_a_clipped_message_turns_an_answer_into_a_fault`).
  const [provisionResult, setProvisionResult] =
    React.useState<CreateProjectResult | null>(null);
  const createOpenAiProject = React.useCallback(async () => {
    const res = await api.createOpenAiProject(id);
    setProvisionResult(res);
    if (res.state === "ready") retry();
    return res;
  }, [api, id, retry]);

  return (
    <section>
      <PeriodBar
        period={period}
        onChange={setPeriod}
        resolved={state.t === "ok" ? state.data.period : null}
        refreshedAt={refreshedAt}
      />

      <ProvisionResultNote result={provisionResult} />

      {state.t === "loading" && <Loading label="Reading recorded AI usage…" />}
      {state.t === "err" && <ErrorState err={state.err} onRetry={retry} />}
      {state.t === "ok" && (
        <Report
          api={api}
          r={state.data}
          onCreateOpenAiProject={createOpenAiProject}
          openCourse={openCourse}
          onOpenCourse={setOpenCourse}
          status={
            importStatus.state.t === "ok" ? importStatus.state.data : null
          }
          statusError={importStatus.state.t === "err"}
        />
      )}
    </section>
  );
}

/** `HH:MM` in the reader's own clock — a time, never a "2 minutes ago". */
function clockTime(d: Date): string {
  return d.toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

function PeriodBar({
  period,
  onChange,
  resolved,
  refreshedAt,
}: {
  period: PeriodId;
  onChange: (p: PeriodId) => void;
  resolved: { from: string; to: string } | null;
  refreshedAt: Date | null;
}) {
  const startsBeforeLedger =
    resolved !== null && new Date(resolved.from) < LEDGER_START;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        marginBottom: 14,
        flexWrap: "wrap",
      }}
    >
      <label
        htmlFor="ai-cost-period"
        style={{ fontSize: 12, color: "var(--text-muted)" }}
      >
        Reporting period
      </label>
      <select
        id="ai-cost-period"
        className="focusable"
        value={period}
        onChange={(e) => onChange(e.target.value as PeriodId)}
        style={{
          fontFamily: "inherit",
          fontSize: 12.5,
          padding: "5px 8px",
          border: "1px solid var(--border)",
          borderRadius: "var(--radius-sm)",
          background: "var(--surface)",
          color: "var(--text)",
        }}
      >
        {PERIODS.map((p) => (
          <option key={p.id} value={p.id}>
            {p.label}
          </option>
        ))}
      </select>

      {/* ★ The RESOLVED window, rendered from what the gateway echoed back and
          not from this component's own arithmetic — so the dates on screen are
          always the dates the figures were computed over. */}
      {resolved && (
        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          {fmtDate(resolved.from)} – {fmtDate(resolved.to)}
        </span>
      )}
      {startsBeforeLedger && (
        <span style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
          Records begin {fmtDate(LEDGER_START.toISOString())}
        </span>
      )}

      {/* ★ THE PROOF THAT THE PAGE IS LIVE — and the only way a reader can tell
          a screen that has quietly stopped updating from one with nothing new to
          show. This page now refreshes itself, so "is this current?" becomes a
          real question the moment the automatic refresh fails; a clock time that
          stops advancing answers it without anyone having to reload to find out.
          ⚠️ A TIME, not "updated just now": a relative phrase is itself stale the
          second it is painted, and it never stops looking fresh. */}
      {refreshedAt && (
        <span
          style={{
            fontSize: 11.5,
            color: "var(--text-faint)",
            marginLeft: "auto",
          }}
          title="These figures refresh automatically while this page is open."
        >
          Updated {clockTime(refreshedAt)}
        </span>
      )}
    </div>
  );
}

function Report({
  api,
  r,
  openCourse,
  onOpenCourse,
  status,
  statusError,
  onCreateOpenAiProject,
}: {
  api: ConsoleApi;
  r: AiCostReport;
  onCreateOpenAiProject: () => Promise<CreateProjectResult>;
  openCourse: string | null;
  onOpenCourse: (id: string | null) => void;
  status: CostImportStatus | null;
  statusError: boolean;
}) {
  const course =
    openCourse === null
      ? null
      : (r.byCourse.find((c) => c.key === openCourse) ?? null);

  // ★ THE DRILL-IN READS THE SAME PAYLOAD. No second request: a second one can
  //   come back with a different total from the row that was just clicked, and
  //   then neither figure is wrong and nobody can tell which to trust.
  const groups: readonly AiCostGroup[] =
    course === null
      ? r.byOperation
      : r.byCourseOperation.filter((g) => g.courseId === course.key);

  const totals: AiCostTotals = course ?? r.totals;
  // ★ The money that belongs to THIS scope. At company level it is what the
  //   providers charged. Inside one course it is that course's SHARE, which the
  //   headings say in as many words — the two are never given the same name
  //   (see MONEY_WORDS).
  // ⚠️ TOLERATES A GATEWAY THAT HAS NOT DEPLOYED YET. The Console (Cloudflare
  //    Pages) and the gateway (Railway) deploy from the same merge but NOT at
  //    the same moment. For the minutes in between, this screen can be the new
  //    one talking to the old API, where `courseCharges` does not exist — and
  //    reading `.byCategory` off `undefined` would replace the whole tab with a
  //    blank panel rather than degrade. Empty is the honest fallback: every
  //    category then reads "Awaiting provider cost", which is what an
  //    un-deployed importer genuinely looks like.
  const courseCharges = r.courseCharges ?? {
    byCategory: [],
    byCourse: [],
    unattributedShare: [],
  };
  const charges: Array<{ key: string; charged: ChargedAmount[] }> =
    course === null
      ? // ★ FALLS BACK TO THE PROVIDER LINES THEMSELVES when the gateway sent no
        //   per-category breakdown. This matters more since the two tables were
        //   merged (2026-09-16): the provider table used to read `providerCost`
        //   directly, so a charge always had somewhere to appear. Without this, a
        //   gateway that predates `courseCharges` would show its money in the
        //   footer total and in NO row — a table that does not add up to its own
        //   total, which is the kind of thing that costs trust in every other
        //   number on the page.
        courseCharges.byCategory.length > 0
        ? courseCharges.byCategory
        : chargesFromProviderLines(r.providerCost)
      : courseCharges.byCourse
          .filter((c) => c.courseId === course.key)
          .map((c) => ({ key: c.key, charged: c.share }));
  // ★ The coverage is a COMPANY-level fact and applies unchanged inside a
  //   course: if Google has not reported past the 15th, it has not reported
  //   past the 15th for any course. Passing it in both views means an image
  //   generated today reads "pending" wherever it is looked at.
  // ★ One lookup, shared by every row: which lines this provider sent for this
  //   window, plus the import's own health. `providerCostState` already ranks
  //   these — an amount that arrived outranks every explanation for why one
  //   might not have (`feedback_absence_and_emptiness_read_the_same`).
  const rows = categoryRows(
    groups,
    charges,
    r.chargeCoverage ?? [],
    (provider) =>
      providerCostState(
        r.providerCost.filter((x) => x.provider === provider),
        status,
        provider,
      ),
    (provider) =>
      status?.providers.find((x) => x.provider === provider) ?? null,
    statusError,
  );

  // Escape leaves the course view — a keyboard exit that is not a small link.
  React.useEffect(() => {
    if (course === null) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onOpenCourse(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [course, onOpenCourse]);

  const nothingAtAll = r.totals.requests === 0;

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <SummaryBand
        totals={totals}
        rows={rows}
        courseLabel={course ? course.label : null}
        // The badge follows the label into the drill-in: the line naming which
        // course these figures belong to must carry the same fact the row did,
        // or opening the course silently drops "Deleted by client" from view.
        courseDeletedByClient={course?.deletedByClient ?? null}
        onBack={() => onOpenCourse(null)}
      />

      {nothingAtAll ? (
        <>
          <p
            style={{
              margin: 0,
              fontSize: 12.5,
              lineHeight: 1.6,
              color: "var(--text-muted)",
            }}
          >
            No AI requests were recorded for this client between{" "}
            {fmtDate(r.period.from)} and {fmtDate(r.period.to)}.
          </p>
          {/* ★ THE COURSES STILL LIST, all at zero (Omar, 2026-09-16: "Even if
              a course didn't spend any money, please list it there, with the 0
              costs"). Without this, a company that has courses but has spent
              nothing renders exactly like a company with no courses at all —
              and this tab is the only place in the Console that names a
              company's courses. The sentence above is kept BECAUSE the table is
              now there: it is what distinguishes "these courses spent nothing"
              from "this table failed to load"
              (`feedback_absence_and_emptiness_read_the_same`).
              The CategoryTable is deliberately NOT shown — every one of its
              rows would be zero, which is the sentence above in five lines. */}
          {course === null && <CourseTable r={r} onOpen={onOpenCourse} />}
        </>
      ) : (
        <>
          {/* ★ ONE LINE, ABOVE THE ONE TABLE — Omar's mock-up, 2026-09-16. It
              used to sit above a SECOND table further down, which is where he
              had to go looking for it. Company view only: it describes an import
              that runs per company, and repeating it inside a course would imply
              the course has its own. */}
          {course === null && (
            <CollectionSchedule status={status} statusError={statusError} />
          )}
          <CategoryTable
            rows={rows}
            totals={totals}
            providerCost={course === null ? r.providerCost : []}
          />
          {course === null ? (
            <CourseTable r={r} onOpen={onOpenCourse} />
          ) : (
            course.requests === 0 && (
              <p
                style={{
                  margin: 0,
                  fontSize: 12.5,
                  color: "var(--text-muted)",
                }}
              >
                This course recorded no AI usage between{" "}
                {fmtDate(r.period.from)} and {fmtDate(r.period.to)}.
              </p>
            )
          )}
        </>
      )}

      {/* ★ COMPANY VIEW ONLY, and that is a statement about what the data can
          support rather than a layout preference. A provider bills a project,
          and the only thing that divides its invoice between clients is the tag
          on each request — there is nothing in it that divides a client's own
          bill between their courses. Showing it under an open course would put
          a company-level figure under a course heading, which reads as that
          course's charge. Omar: "Per-course reporting remains optional and must
          not block this verification." */}
      {course === null && (
        <>
          {/* ⚠️ `ProviderCostSection`'s TABLE is gone — merged into the one above
              (Omar, 2026-09-16). What it uniquely carried has moved rather than
              been dropped: the provider name is now each row's heading, the
              per-currency totals are that table's footer, and the reason a
              provider reported nothing is its row's own money cell. This note is
              what remains — the OpenAI setup explanation, which is not a figure
              and has no row to live in. */}
          <OpenAiSetupNote
            setup={r.openAiSetup}
            onCreate={onCreateOpenAiProject}
          />
          <AttributionGaps api={api} r={r} />
        </>
      )}

      <Provenance r={r} status={status} />
    </div>
  );
}

// ── The summary band ────────────────────────────────────────────────────────

// ⚠️ `r` (the whole report) was a prop until 2026-09-14 and is not one any
// more: its only use here was the "All courses · <from> – <to>" line, which
// repeated the reporting-period control directly above it. A prop kept "in case"
// is how a component quietly regains a dependency it does not have.
function SummaryBand({
  totals,
  rows,
  courseLabel,
  courseDeletedByClient = null,
  onBack,
}: {
  totals: AiCostTotals;
  rows: CategoryRow[];
  courseLabel: string | null;
  /** The drilled-in course's recorded deletion, when it has one — see the
   *  `deletedByClient` note on AiCostGroup in api.ts. Null at company level. */
  courseDeletedByClient?: AiCostGroup["deletedByClient"] | null;
  onBack: () => void;
}) {
  const empty = totals.requests === 0;
  // ★ THE HEADLINE FIGURE IS THE PROVIDERS' OWN. Omar, 2026-09-11: "Remove the
  //   estimated-cost headline. Lead the page with provider-reported costs. I do
  //   not need estimated costs in this view, and the current $0.00 headline is
  //   misleading." It read $0.00 because our own estimate is deliberately blank
  //   for question generation and subtitles — no rate has ever been measured for
  //   either — so the biggest number on the page was the one thing that could
  //   never be right.
  const charged = sumCharged(rows.map((x) => x.charged));
  const awaitingNames = rows
    .filter(
      (x) => x.chargeState === "awaiting" && x.group && x.group.requests > 0,
    )
    .map((x) => x.category.label);
  // ★ Is there anything ABNORMAL to report? The slot below renders only then.
  const hasNote = empty || awaitingNames.length > 0 || charged.length === 0;

  return (
    <div className="card" style={{ padding: "16px 18px" }}>
      <div
        style={{
          display: "flex",
          gap: 24,
          alignItems: "flex-start",
          flexWrap: "wrap",
        }}
      >
        <div style={{ flex: "1 1 280px" }}>
          <div
            style={{
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              fontWeight: 600,
              color: "var(--text-faint)",
            }}
          >
            {courseLabel === null
              ? MONEY_WORDS.companyHeading
              : MONEY_WORDS.courseHeading}
          </div>
          <div
            style={{
              fontSize: empty || charged.length === 0 ? 20 : 30,
              fontWeight: 600,
              // ★ Not "$0.0000". An empty period is a fact about the RECORD; a
              //   zero is a claim about spending
              //   (`feedback_absence_and_emptiness_read_the_same`).
              color: empty
                ? "var(--text-muted)"
                : charged.length === 0
                  ? "var(--warning-text)"
                  : "var(--text-strong)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "-0.02em",
              marginTop: 2,
            }}
          >
            {/* ★ THE USAGE COST, not the net (Omar, 2026-09-16). This line read
                `0.00 EUR` while EUR 0.46 of image generation had been billed and
                entirely covered by credit — the most prominent place on the tab
                where "fully discounted" and "nothing happened" were rendered
                identically. The credit and the payable follow underneath, and
                only when there IS a credit. */}
            {empty
              ? "Nothing recorded"
              : charged.length === 0
                ? "Awaiting provider cost"
                : charged.map((c) => (
                    <div key={c.currency}>
                      {providerAmount(c.gross ?? c.amount, c.currency)}
                    </div>
                  ))}
          </div>

          {/* The other two figures, only where a credit actually exists. */}
          {!empty &&
            charged
              .filter((c) => c.credit !== undefined && c.credit !== 0)
              .map((c) => (
                <div
                  key={`${c.currency}-credit`}
                  style={{
                    fontSize: 12,
                    color: "var(--text-muted)",
                    marginTop: 2,
                    fontVariantNumeric: "tabular-nums",
                  }}
                >
                  {providerAmount(c.credit as number, c.currency)} credits ·{" "}
                  {providerAmount(c.amount, c.currency)} payable
                </div>
              ))}

          <div
            style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 4 }}
          >
            {/* ⚠️ Nothing for the all-courses view since 2026-09-14 (Omar).
                It read "All courses · 15 Aug – 14 Sept", which is what the
                reporting-period control immediately above already says; the
                figure is the company's whole spend unless a course is open. The
                DRILLED-IN form stays: it is the only thing naming which course
                the number belongs to, and it carries the way back. */}
            {courseLabel === null ? null : (
              <>
                {/* Back lives in the line that names what the figures describe,
                    because changing scope and going back are the same act. */}
                <button
                  className="focusable"
                  onClick={onBack}
                  style={{
                    appearance: "none",
                    background: "transparent",
                    border: 0,
                    padding: 0,
                    marginRight: 6,
                    fontFamily: "inherit",
                    fontSize: 12.5,
                    fontWeight: 500,
                    color: "var(--text)",
                    cursor: "default",
                    textDecoration: "underline",
                  }}
                >
                  ← All courses
                </button>
                · {courseLabel}
                {/* Same badge, same reason as the row it was opened from. */}
                {courseDeletedByClient ? (
                  <span
                    className="pill pending"
                    style={{ marginLeft: 8, verticalAlign: "middle" }}
                  >
                    Deleted by client
                  </span>
                ) : null}
              </>
            )}
          </div>

          {/* ⚠️ REMOVED 2026-09-12, Omar's instruction: "I do not want estimated
              amounts presented alongside provider-reported costs."

              This line read "of $X across all courses", and that $X was
              `r.totals.estimatedCostUsd` — DYNAMO'S OWN ESTIMATE, list price ×
              usage. It sat directly above the provider-reported figures, which
              is exactly the juxtaposition he ruled out on 2026-09-08: "Do not
              present a calculation based on usage multiplied by list prices as
              an actual provider charge." A denominator is only useful if it is
              the same KIND of number as the figure it scales, and this one was
              not (`feedback_honest_gates_over_standins`).

              It was added to stop a course figure being read without context.
              That concern was real; the remedy was the wrong number. Nothing
              replaces it here — the per-course breakdown below already shows
              every course, so the reader can see the whole without being handed
              an estimate dressed as a total. */}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, auto)",
            gap: "0 20px",
          }}
        >
          <BandCount label="AI requests" value={count(totals.requests)} />
          <BandCount
            label="Paid calls"
            value={count(totals.attempts)}
            hint={
              totals.attempts > totals.requests
                ? `${count(totals.attempts - totals.requests)} were retries`
                : undefined
            }
          />
          <BandCount
            label="Discarded"
            value={count(totals.unusable)}
            hint={totals.unusable > 0 ? "paid for, not used" : undefined}
          />
        </div>
      </div>

      {/* ★ THE SLOT SPEAKS ONLY WHEN THERE IS SOMETHING TO SAY.
          It used to always carry a statement, on the reasoning that an empty
          slot reads as "we did not check". Omar, 2026-09-14, reviewing the
          screen: "keep only the text that [is] showed in [the image]", naming
          this sentence first. "Every provider that can bill this client per
          client has reported for this period" is the HEALTHY case — nothing a
          reader can act on, re-read on every visit. The sentences that remain
          each report an abnormal state, so the slot is now silent exactly when
          silence is the truth, and the divider goes with it. */}
      {hasNote && (
        <div
          style={{
            marginTop: 14,
            paddingTop: 12,
            borderTop: "1px solid var(--border-faint)",
            display: "grid",
            gap: 6,
          }}
        >
          {empty ? (
            <p
              style={{
                margin: 0,
                fontSize: 12,
                lineHeight: 1.55,
                color: "var(--text-muted)",
              }}
            >
              No AI requests were recorded in this period.
            </p>
          ) : awaitingNames.length > 0 ? (
            <p
              style={{
                margin: 0,
                fontSize: 12,
                lineHeight: 1.55,
                color: "var(--warning-text)",
              }}
            >
              {/* ★ THIS IS WHAT MAKES THE TOTAL HONEST WHEN IT IS PARTIAL. Omar,
                2026-09-11: "identify totals as partial when some charges are
                still unavailable." Generated from state, never hardcoded, so it
                stops naming a category on the day that category is billed. */}
              Partial. {listOf(awaitingNames)}{" "}
              {awaitingNames.length === 1 ? "has" : "have"} been used in this
              period and the provider has not published{" "}
              {awaitingNames.length === 1 ? "its charge" : "their charges"} yet,
              so {awaitingNames.length === 1 ? "it is" : "they are"} not in the
              figure above.
            </p>
          ) : (
            <p
              style={{
                margin: 0,
                fontSize: 12,
                lineHeight: 1.55,
                color: "var(--text-muted)",
              }}
            >
              Nothing has been billed for this period.
            </p>
          )}

          {/* ⚠️ REMOVED 2026-09-14 — IT HAD BECOME FALSE, and it was the most
            misleading thing on the screen.

            It read: "These figures are this system's own record of what this
            client used. Google bills every client together on one shared
            project and cannot split that bill per client." Its premise was true
            when written — an API key attributes a request to a PROJECT — and
            stopped being true on 2026-09-11, when a service account replaced the
            keys and Translation v3 and Vertex AI began carrying the client
            label. `ai-cost-categories.ts` was corrected that day; this sentence
            was not (`feedback_a_renames_leftovers_live_on_the_screens_you_did_not_rename`).

            ★ Measured 2026-09-14, all three environments: MOHG's OWN Google rows
            are present and labelled — Vertex AI 0.289883 EUR gross over 4 lines,
            Translate 0.032628 EUR gross over 2. So the screen was denying the
            existence of figures printed a few centimetres above it, which is
            exactly what makes a correct zero read as a broken import. */}
        </div>
      )}
    </div>
  );
}

function listOf(names: string[]): string {
  if (names.length === 1) return names[0] as string;
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1] as string}`;
}

function BandCount({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string | undefined;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          fontWeight: 600,
          color: "var(--text-faint)",
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 15,
          fontWeight: 600,
          color: "var(--text)",
          fontVariantNumeric: "tabular-nums",
          marginTop: 2,
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 11, color: "var(--text-muted)", minHeight: 15 }}>
        {hint ?? ""}
      </div>
    </div>
  );
}

// ── The category table ──────────────────────────────────────────────────────

function CategoryTable({
  rows,
  totals,
  providerCost = [],
}: {
  rows: CategoryRow[];
  totals: AiCostTotals;
  /**
   * The providers' own lines, for the footer's per-currency subtotals.
   *
   * ★ NOT the same arithmetic as `chargedTotal` below, and both are wanted.
   *   `chargedTotal` adds up what the FIVE ROWS show, so it always agrees with
   *   the table above it. `providerCost` is every line the providers sent,
   *   INCLUDING the ones no category could be derived from — Dynamo's own
   *   BigQuery bill, a service no rule recognises yet. A footer built only from
   *   the rows would silently drop those and still call itself a total
   *   (`feedback_an_empty_list_may_mean_everything`).
   *
   * Empty inside a course view, where a company-level invoice does not belong.
   */
  providerCost?: readonly AiProviderCostRow[];
}) {
  const chargedTotal = sumCharged(rows.map((r) => r.charged));
  const currencyTotals = totalsByCurrency(providerCost);
  // ★ Which categories were USED but have no figure yet. This is what stops the
  //   total reading as complete when part of the period is still outstanding —
  //   the same job `requestsWithoutCost` used to do for the estimate.
  const awaiting = rows
    .filter(
      (r) => r.chargeState === "awaiting" && r.group && r.group.requests > 0,
    )
    .map((r) => r.category.label);
  const border = "1px solid var(--border)";

  return (
    <div className="console-table-wrap">
      <table className="console-table">
        <thead>
          <tr>
            {/* Empty on purpose: "Category" over a column of category names is a
                label for something already obvious, and costs a line of
                uppercase noise at the top of the primary table. */}
            <th style={{ width: "42%" }} />
            <th style={{ textAlign: "right" }}>Usage</th>
            <th style={{ textAlign: "right" }}>Requests</th>
            <th style={{ textAlign: "right" }}>{MONEY_WORDS.column}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <CategoryRowView key={row.category.key} row={row} />
          ))}
        </tbody>
        <tfoot>
          <tr data-static="">
            <td data-label="" style={{ borderTop: border }}>
              <div
                style={{
                  fontSize: 12.5,
                  fontWeight: 600,
                  color: "var(--text)",
                }}
              >
                {/* Omar's mock-up names this row "Totals, per currency" — the
                    wording the merged-away table used, kept because it is the
                    one that explains why there can be two lines under it. */}
                {currencyTotals.length > 1 ? "Totals, per currency" : "Total"}
              </div>
              {awaiting.length > 0 && (
                <div
                  style={{
                    fontSize: 11.5,
                    color: "var(--warning-text)",
                    marginTop: 2,
                  }}
                >
                  Partial — {listOf(awaiting)}{" "}
                  {awaiting.length === 1 ? "has" : "have"} been used but not yet
                  billed.
                </div>
              )}
            </td>
            {/* ★ NO TOTAL FOR USAGE. Summing images, characters and audio
                seconds produces a number that means nothing. */}
            <td data-label="Usage" style={{ borderTop: border }} />
            <td
              data-label="Requests"
              className="console-num"
              style={{ borderTop: border, textAlign: "right" }}
            >
              {count(totals.requests)}
            </td>
            <td
              data-label={MONEY_WORDS.column}
              className="console-num"
              style={{
                borderTop: border,
                textAlign: "right",
                fontSize: 14,
                fontWeight: 600,
                color: "var(--text-strong)",
              }}
            >
              {(currencyTotals.length > 0 ? currencyTotals : chargedTotal)
                .length === 0 ? (
                <span
                  style={{
                    color: "var(--text-faint)",
                    fontWeight: 400,
                    fontSize: 12.5,
                  }}
                >
                  {totals.requests === 0
                    ? "Nothing recorded"
                    : "Awaiting provider cost"}
                </span>
              ) : (
                <div style={{ display: "grid", gap: 6 }}>
                  {(currencyTotals.length > 0
                    ? currencyTotals
                    : chargedTotal
                  ).map((c) => (
                    <MoneyBreakdown key={c.currency} money={c} />
                  ))}
                </div>
              )}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function CategoryRowView({ row }: { row: CategoryRow }) {
  const { category, group, state } = row;
  const dim = state === "unavailable";

  return (
    // `data-static` — these rows are not clickable, and the only clickable rows
    // on this screen are courses. That difference has to be visible.
    <tr data-static="">
      <td data-label="">
        <div>
          {/* ── ★ THE PROVIDER LEADS, THE FEATURE FOLLOWS (Omar, 2026-09-16) ──
              "I have only one Service Table with the Services split by feature
              (Google Image, Google Translate, Google Video, etc)."

              This table used to name the feature and nothing else, while a
              SECOND table named the provider and nothing else, so telling which
              vendor billed a feature meant reading both and joining them by
              memory. One row now carries both facts.

              ⚠️ `billedBy` is null for Video — not built, and its provider has
                 never been chosen. Omar's mock-up drew it as "Google / Video
                 generation"; printing a vendor beside an unbuilt feature states
                 a decision nobody made, so the row shows the feature alone and
                 still reads "Not available"
                 (`feedback_a_plausible_placeholder_is_worse_than_an_obvious_one`). */}
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: dim ? "var(--text-muted)" : "var(--text)",
            }}
          >
            {category.billedBy ? category.billedBy.label : category.label}
          </div>
          {category.billedBy && (
            <div
              style={{
                fontSize: 11.5,
                color: "var(--text-muted)",
                marginTop: 1,
              }}
            >
              {category.label}
            </div>
          )}
          {/* ⚠️ The five permanent categories no longer carry one (Omar,
              2026-09-14: "The current text descriptions on the AI costs section
              do not add any value. It is too wordy and the page is complex to
              understand."). Only `other` does, because that row appears solely
              when something unrecognised has landed in it and its sentence
              explains the surprise. */}
          {category.description ? (
            <div
              style={{
                fontSize: 11.5,
                color: "var(--text-muted)",
                marginTop: 2,
              }}
            >
              {category.description}
            </div>
          ) : null}
          {state === "unpriced" && (
            <span
              className="pill issues"
              style={{ marginTop: 6, display: "inline-flex" }}
            >
              Not yet priced
            </span>
          )}
          {state === "unavailable" && (
            // ★ GREY, NOT AMBER. Nothing is wrong: the service does not exist.
            //   Amber would ask Omar to act on something that is not a problem.
            <span
              className="pill pending"
              style={{ marginTop: 6, display: "inline-flex" }}
            >
              Not available yet
            </span>
          )}
        </div>
      </td>

      <td
        data-label="Usage"
        className="console-num"
        style={{ textAlign: "right" }}
      >
        <UsageCell group={group} state={state} />
      </td>

      <td
        data-label="Requests"
        className="console-num"
        style={{ textAlign: "right" }}
      >
        {state === "unavailable" ? (
          <span style={{ color: "var(--text-faint)", fontSize: 12.5 }}>
            Not available
          </span>
        ) : state === "empty" ? (
          <span style={{ color: "var(--text-faint)" }}>—</span>
        ) : (
          <>
            <div>{count(group ? group.requests : 0)}</div>
            {group && group.attempts > group.requests && (
              <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                +{count(group.attempts - group.requests)} retries
              </div>
            )}
          </>
        )}
      </td>

      <td
        data-label={MONEY_WORDS.column}
        className="console-num"
        style={{ textAlign: "right" }}
      >
        <ChargeCell row={row} />
      </td>
    </tr>
  );
}

function UsageCell({
  group,
  state,
}: {
  group: AiCostGroup | null;
  state: CategoryState;
}) {
  if (state === "unavailable") {
    // Words, not a dash: below 700px this cell becomes its own labelled line,
    // detached from the row, and has to read on its own.
    return (
      <span style={{ color: "var(--text-faint)", fontSize: 12.5 }}>
        Not available
      </span>
    );
  }
  if (!group || state === "empty")
    return <span style={{ color: "var(--text-faint)" }}>—</span>;

  if (group.units.length > 1) {
    // ★ REFUSES TO PRINT A MEANINGLESS SUM. `billableUnits` here would be
    //   characters ADDED TO images. Each unit gets its own line instead.
    return (
      <div>
        {group.units.map((u) => (
          <div key={u.unit} style={{ fontSize: 12.5 }}>
            {humanUnit(u.unit, u.amount)}
          </div>
        ))}
      </div>
    );
  }
  if (group.units.length === 1) {
    const u = group.units[0] as { unit: string; amount: number };
    return <span style={{ fontSize: 13 }}>{humanUnit(u.unit, u.amount)}</span>;
  }
  if (group.totalTokens > 0) {
    return (
      <span style={{ fontSize: 13 }}>{count(group.totalTokens)} tokens</span>
    );
  }
  // Recorded, but this kind of work records no volume. Not a zero.
  return (
    <span style={{ color: "var(--text-faint)", fontSize: 12.5 }}>
      Not measured
    </span>
  );
}

/**
 * The money column, which now shows what the PROVIDER charged.
 *
 * ★ It used to show our own estimate, which for question generation and
 * subtitles was permanently "Not yet priced" — nobody had measured a per-token
 * or per-minute rate, and the system refuses to invent one. So the column read
 * as broken while the providers' real figures sat imported one table further
 * down (Omar, 2026-09-11: "display the cost per Question and text generation,
 * Subtitle generation").
 *
 * Omar's warning in the same message — "changing an estimated figure's label
 * does not make it a provider-reported charge" — is why the SOURCE moved and not
 * just the heading. Nothing on this screen is calculated from a price list any
 * more.
 */
function ChargeCell({ row }: { row: CategoryRow }) {
  const { chargeState, charged } = row;

  if (chargeState === "not_built") {
    return (
      <span style={{ color: "var(--text-faint)", fontSize: 12.5 }}>
        Not available
      </span>
    );
  }

  if (chargeState === "not_per_client") {
    // ★ NOT "awaiting". This state says a per-client figure is not late — it is
    //   impossible — so the wording must never promise a number.
    //
    // ⚠️ WHO REACHES THIS NOW. Until 2026-09-11 it was translation and image
    //    generation, on the reasoning that Google bills every client to one
    //    shared project. A service account ended that: both services now carry
    //    a client label and both are `per_client`, so they await a figure like
    //    anything else. What is left here is "Other AI usage" — work whose
    //    provider we cannot name, which is why no provider's charge can be
    //    matched to it. The copy below used to say "One shared project", which
    //    was about Google and is simply false of this row
    //    (`feedback_a_readable_label_can_name_the_wrong_thing`).
    return (
      <div>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          Not billed per client
        </div>
        <div style={{ fontSize: 11.5, color: "var(--text-faint)" }}>
          No provider identified
        </div>
      </div>
    );
  }

  if (chargeState === "empty") {
    // ★ A BROKEN IMPORT IS SHOWN EVEN WITH NO USAGE, and nothing else is.
    //
    // The 2026-09-11 rule — "nothing recorded beats every explanation of why
    // there is no figure" — is right for `not_connected`, `off` and `unknown`:
    // this company used none of it, so no explanation is owed. It is WRONG for
    // `broken`, which is a fault somebody must fix, and "Nothing recorded"
    // conceals it behind a sentence that looks like good news.
    //
    // ⚠️ THE MERGE MADE THIS NECESSARY. Until 2026-09-16 a separate per-provider
    //    table carried the fault regardless of usage; folding it into the
    //    category rows would otherwise have hidden a revoked credential on every
    //    page where that feature happened to be unused
    //    (`feedback_absence_and_emptiness_read_the_same`).
    if (row.providerState === "broken") {
      return (
        <div style={{ textAlign: "left" }}>
          <ProviderCostPlaceholder state="broken" />
          <ProviderStatusLine
            state="broken"
            status={row.providerStatus}
            statusError={row.statusUnreadable}
          />
        </div>
      );
    }
    // A statement about the RECORD, not about spending.
    return (
      <span style={{ color: "var(--text-faint)", fontSize: 12.5 }}>
        Nothing recorded
      </span>
    );
  }

  if (chargeState === "awaiting") {
    // ★ NEVER `$0.00`. A zero reads as "free"; this reads as "not in yet".
    //
    // ★ AND IT SAYS *WHY*, when the reason is not simply "the provider has not
    //   published yet". A revoked credential and an unpublished window look
    //   identical from here, and only one of them needs somebody to act. This
    //   is what the merged-away provider table carried; folding it in without
    //   this would have been a real loss dressed as a simplification.
    if (row.providerState && row.providerState !== "awaiting") {
      return (
        <div style={{ textAlign: "left" }}>
          <ProviderCostPlaceholder state={row.providerState} />
          <ProviderStatusLine
            state={row.providerState}
            status={row.providerStatus}
            statusError={row.statusUnreadable}
          />
        </div>
      );
    }
    return (
      <div style={{ textAlign: "left" }}>
        <div
          style={{
            fontSize: 12.5,
            fontWeight: 500,
            color: "var(--warning-text)",
            textAlign: "right",
          }}
        >
          Awaiting provider cost
        </div>
        <div
          style={{
            fontSize: 11.5,
            color: "var(--text-muted)",
            textAlign: "right",
          }}
        >
          Used, not yet billed
        </div>
        {/* ★ ONLY WHEN THERE IS MORE TO SAY THAN "not in yet". A healthy import
            waiting on a provider needs no sentence — that is what the two lines
            above already mean, and Omar removed the verbose version on
            2026-09-14. `stale` and `never_run` are different: one says the wait
            is longer than it should be, the other that no import has ever run,
            and neither is visible from "Awaiting provider cost". */}
        {(row.providerStatus?.health === "stale" ||
          row.providerStatus?.health === "never_run") && (
          <ProviderStatusLine
            state="awaiting"
            status={row.providerStatus}
            statusError={row.statusUnreadable}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {charged.map((c) => (
        <MoneyBreakdown key={c.currency} money={c} />
      ))}
      {row.pending && <PendingNote reportedThrough={row.reportedThrough} />}
      {/* ★ SILENT WHEN THE IMPORT IS SIMPLY WORKING, and not silent when the
          most recent refresh FAILED — a figure on screen that can no longer be
          updated. The strip at the top of the tab cannot say that per provider,
          which is why this survived the merge. */}
      {row.providerState && (
        <div style={{ textAlign: "left" }}>
          <ProviderStatusLine
            state={row.providerState}
            status={row.providerStatus}
            statusError={row.statusUnreadable}
          />
        </div>
      )}
    </div>
  );
}

/**
 * One currency's money, as THREE figures rather than one.
 *
 * ── Omar, 2026-09-16 (verbatim) ─────────────────────────────────────────────
 *
 *   "I want to see the usage cost reported by Google BEFORE credits are
 *    applied. Promotional credits explain how the cost is covered; they must not
 *    hide the cost of using the service … Any credits and net payable amount
 *    should be clearly distinguished."
 *
 * ★ WHY THE USAGE COST IS THE BIG NUMBER. Every Google image euro on this
 *   account is inside the free tier, so the net is 0.00 and always has been —
 *   printing that alone rendered the whole Google picture as "nothing happened"
 *   while real consumption was being billed and discounted
 *   (`feedback_a_comparison_of_two_zeros_is_not_a_comparison`).
 *
 * ★ AND WHY THE OTHER TWO LINES ONLY APPEAR WHEN THERE IS A CREDIT. A row
 *   reading "usage 0.12 · credits 0.00 · payable 0.12" says the same thing three
 *   times. `credit === undefined` means the provider applied none — or, on a
 *   gateway that predates migration 0023, that it could not tell us; both are
 *   "no credit information" and neither is a credit of zero
 *   (`feedback_absence_and_emptiness_read_the_same`).
 */
function MoneyBreakdown({ money }: { money: ChargedAmount }) {
  const gross = money.gross ?? money.amount;
  const credited = money.credit !== undefined && money.credit !== 0;
  return (
    <div>
      <div
        style={{ fontSize: 14, fontWeight: 600, color: "var(--text-strong)" }}
      >
        {providerAmount(gross, money.currency)}
      </div>
      {credited && (
        <>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
            {providerAmount(money.credit as number, money.currency)} credits
          </div>
          <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
            {providerAmount(money.amount, money.currency)} payable
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Recorded work the provider has not billed for yet.
 *
 * ★ SHOWN BESIDE A FIGURE, not instead of it. Omar, 2026-09-16: "Until Google
 *   publishes the new billing data, keep that operation pending; an existing
 *   zero balance does not confirm it has been reported." The figure above is
 *   real and the work below it is not in that figure — both are true, and a
 *   screen that could only say one of them said the reassuring one.
 */
function PendingNote({ reportedThrough }: { reportedThrough: string | null }) {
  return (
    <div style={{ fontSize: 11.5, color: "var(--warning-text)" }}>
      {reportedThrough === null
        ? "Newer usage not yet billed"
        : `Newer usage not yet billed — reported to ${fmtDate(reportedThrough)}`}
    </div>
  );
}

/**
 * Group the providers' own lines by the category the gateway placed them in.
 *
 * ★ ONLY A FALLBACK. When `courseCharges` is present it is the gateway's own
 *   arithmetic and wins — two implementations of one sum would be two answers
 *   (`feedback_a_test_double_that_reimplements_drifts`).
 *
 * ⚠️ A line the gateway could NOT place (`category: null`) is deliberately
 *    dropped here rather than guessed into a row. It is not lost: the footer's
 *    per-currency totals are built from EVERY line, so an unplaceable charge —
 *    Dynamo's own BigQuery bill, a service no rule recognises yet — still shows
 *    up in the money. The gap between the rows and the total is real and worth
 *    seeing (`feedback_an_empty_list_may_mean_everything`).
 */
function chargesFromProviderLines(
  rows: readonly AiProviderCostRow[],
): Array<{ key: string; charged: ChargedAmount[] }> {
  const by = new Map<string, AiProviderCostRow[]>();
  for (const r of rows) {
    if (!r.category) continue;
    by.set(r.category, [...(by.get(r.category) ?? []), r]);
  }
  return [...by.entries()].map(([key, list]) => ({
    key,
    charged: totalsByCurrency(list),
  }));
}

/**
 * Add up money that shares a currency, and never money that does not.
 *
 * ★ CARRIES ALL THREE FIGURES THROUGH (Omar, 2026-09-16). This feeds the big
 *   number at the top of the tab, which read `0.00 EUR` while EUR 0.46 of image
 *   generation had been billed and discounted — the single most prominent place
 *   the old net-only reading was wrong.
 */
export function sumCharged(
  lists: ReadonlyArray<readonly ChargedAmount[]>,
): ChargedAmount[] {
  const acc = new Map<
    string,
    { net: number; gross: number; credit: number | null }
  >();
  for (const list of lists) {
    for (const c of list) {
      const at = acc.get(c.currency) ?? { net: 0, gross: 0, credit: null };
      const credit = c.credit ?? null;
      acc.set(c.currency, {
        net: at.net + c.amount,
        // No gross reported means no credit information, and the honest reading
        // of "what did this cost" is then the net itself — never a discount this
        // screen invented (`feedback_absence_and_emptiness_read_the_same`).
        gross: at.gross + (c.gross ?? c.amount),
        credit: credit === null ? at.credit : (at.credit ?? 0) + credit,
      });
    }
  }
  return [...acc.entries()]
    .map(([currency, p]) => ({
      currency,
      amount: p.net,
      gross: p.gross,
      ...(p.credit === null ? {} : { credit: p.credit }),
    }))
    // Ordered by the GROSS: ordering by the net sorts a fully-credited currency
    // to the bottom in an order that means nothing.
    .sort(
      (a, b) =>
        (b.gross ?? b.amount) - (a.gross ?? a.amount) ||
        a.currency.localeCompare(b.currency),
    );
}

/**
 * One course's SHARE of a charge — never called a charge.
 *
 * ★ SHOWS THE USAGE COST, like every other money cell (Omar, 2026-09-16). A
 *   share of a fully-credited charge is a share of zero, so the net alone made
 *   every course on this screen read 0.00 — which is what the company row above
 *   it also read, and neither said that EUR 0.46 of work had been done.
 *
 * ⚠️ AND IT IS STILL A SHARE. Omar, the same day: "Keep the course breakdown,
 *    clearly identified as Dynamo's calculated allocation … A change in that
 *    allocation alone is not proof of the test image's actual cost." The column
 *    heading says "Share of charge" and the provenance note says whose
 *    calculation it is; nothing here is presented as a provider's own number.
 */
function ShareCell({ share }: { share: readonly ChargedAmount[] }) {
  if (share.length === 0) {
    return (
      <span style={{ color: "var(--text-faint)", fontSize: 12.5 }}>—</span>
    );
  }
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {share.map((c) => (
        <MoneyBreakdown key={c.currency} money={c} />
      ))}
    </div>
  );
}

// ── The course list ─────────────────────────────────────────────────────────

function CourseTable({
  r,
  onOpen,
}: {
  r: AiCostReport;
  onOpen: (id: string) => void;
}) {
  // The API sorts by cost. Work not tied to a course is pinned to the BOTTOM,
  // because it is not a course and sorting it into the middle of a course list
  // asserts that it is. It stays openable — its breakdown is the only way to
  // find out what that spend actually was.
  const courses = [
    ...r.byCourse.filter((c) => c.key !== UNATTRIBUTED),
    ...r.byCourse.filter((c) => c.key === UNATTRIBUTED),
  ];
  if (courses.length === 0) return null;

  // ★ WHY A COURSE HAS A SHARE AND NOT A CHARGE. No provider reports per course
  //   — Deepgram is told a company tag and OpenAI bills a company project — so
  //   this is the company's real charge divided by what each course measurably
  //   used. Computed on the server (`ai-cost-share.ts`) so the split is tested
  //   rather than assembled in JSX.
  const shareOf = (courseKey: string): ChargedAmount[] =>
    sumCharged(
      (r.courseCharges?.byCourse ?? [])
        .filter((c) => c.courseId === courseKey)
        .map((c) => c.share),
    );

  // Which categories a course has work in, in the FIXED category order — so the
  // secondary line answers "why is this course expensive" without a chart.
  const categoriesOf = (courseKey: string): string =>
    categoryRows(r.byCourseOperation.filter((g) => g.courseId === courseKey))
      .filter((x) => x.group !== null && x.group.requests > 0)
      .map((x) => x.category.label)
      .join(" · ");

  // ── ★ TWO DELETED COURSES CAN SHARE A NAME ────────────────────────────────
  //
  // Omar, 2026-09-19, deleting two courses both called "Modern Slavery":
  // "Distinguish the two identically named courses by their IDs." The rows are
  // already apart — `byCourse` is keyed by course id — but the SCREEN showed
  // two identical labels with nothing telling them apart. So a recorded
  // deletion whose label collides with ANY other row's label carries its
  // course id on the secondary line. Counted over labels, not guessed from the
  // deletion list alone, so a deleted "Modern Slavery" beside a live one also
  // shows which is which.
  const labelCount = new Map<string, number>();
  for (const c of courses)
    labelCount.set(c.label, (labelCount.get(c.label) ?? 0) + 1);

  return (
    <div>
      {/* ⚠️ The count-and-instruction line was removed 2026-09-14 (Omar). The
          table header already says "Course", the rows already carry a chevron,
          and the count is the number of rows directly beneath it. */}
      <div className="console-table-wrap">
        <table className="console-table">
          <thead>
            <tr>
              <th style={{ width: "56%" }}>Course</th>
              <th style={{ textAlign: "right" }}>Requests</th>
              <th style={{ textAlign: "right" }}>{MONEY_WORDS.courseColumn}</th>
            </tr>
          </thead>
          <tbody>
            {courses.map((c) => (
              <tr
                key={c.key}
                role="button"
                tabIndex={0}
                onClick={() => onOpen(c.key)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onOpen(c.key);
                  }
                }}
                style={{ cursor: "default" }}
              >
                <td data-label="">
                  <div>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 500,
                        color: "var(--text)",
                      }}
                    >
                      {c.label}
                      {/* ★ ONLY on a RECORDED client deletion — the server
                          sends `deletedByClient` solely for those, and this
                          component never computes the verdict itself. A row
                          deleted before the recording existed keeps its plain
                          "Deleted course" label and gets NO badge: the badge
                          states who deleted it, which for those rows nobody
                          recorded (`feedback_absence_and_emptiness_read_the_same`).
                          Grey (`pending`), not amber: nothing is wrong — the
                          figures are retained on purpose. */}
                      {c.deletedByClient ? (
                        <span
                          className="pill pending"
                          style={{ marginLeft: 8, verticalAlign: "middle" }}
                          title={
                            c.deletedByClient.deletedAt
                              ? // Date part of the ISO stamp, same idiom as
                                // dateWindowOf(): the day is the fact worth
                                // hovering for; the millisecond is not.
                                `Deleted by the client on ${c.deletedByClient.deletedAt.slice(0, 10)}`
                              : undefined
                          }
                        >
                          Deleted by client
                        </span>
                      ) : null}
                      <span
                        aria-hidden="true"
                        style={{ color: "var(--text-faint)", marginLeft: 8 }}
                      >
                        ›
                      </span>
                    </div>
                    {c.deletedByClient &&
                    (labelCount.get(c.label) ?? 0) > 1 ? (
                      <div
                        style={{
                          fontSize: 11,
                          color: "var(--text-faint)",
                          marginTop: 2,
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        Course ID: {c.key}
                      </div>
                    ) : null}
                    <div
                      style={{
                        fontSize: 11.5,
                        color: "var(--text-muted)",
                        marginTop: 2,
                      }}
                    >
                      {categoriesOf(c.key) || "No categorised work"}
                    </div>
                  </div>
                </td>
                <td
                  data-label="Requests"
                  className="console-num"
                  style={{ textAlign: "right" }}
                >
                  {count(c.requests)}
                </td>
                <td
                  data-label={MONEY_WORDS.courseColumn}
                  className="console-num"
                  style={{ textAlign: "right" }}
                >
                  {(() => {
                    const share = shareOf(c.key);
                    return share.length === 0 ? (
                      <span
                        style={{ color: "var(--warning-text)", fontSize: 12.5 }}
                      >
                        Awaiting provider cost
                      </span>
                    ) : (
                      <ShareCell share={share} />
                    );
                  })()}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* ⚠️ "Not tied to a course, so listed on its own." went 2026-09-14
          (Omar). It said, under the table, what the row itself says IN the
          table — the row is literally labelled "Not attributed to a course",
          and it is pinned to the bottom rather than sorted into the middle,
          which is the behaviour the sentence was describing. The guarantee it
          announced (nothing is ever apportioned to a course arbitrarily) is
          enforced on the server in `ai-cost-share.ts` and tested there; it was
          never this footnote that made it true. */}
    </div>
  );
}

// ── What the PROVIDERS charged ───────────────────────────────────────────────
//
// ★ WHAT OMAR ASKED FOR, 2026-09-10 (verbatim):
//
//   "Deepgram supplies the monetary amounts. Each amount is attributed to the
//    correct company. The amounts are visible in the company's AI costs tab.
//    They match Deepgram's report for the same period, currency and scope."
//
//   "Confirm that new costs are imported automatically … Show the last
//    successful update and distinguish between: usage whose cost has not yet
//    been published by the provider; an import that cannot run because access is
//    missing, revoked or expired. The second situation must produce a clear
//    connection error, rather than remain indefinitely labelled 'Awaiting
//    provider cost'."
//
// ── A SEPARATE SECTION, not a fifth column ─────────────────────────────────
//
// A "Provider-reported" column beside the estimate was the first idea and it
// cannot work, for two reasons that are both about the invoice rather than the
// layout:
//
//  1. CURRENCY IS PER ROW. Deepgram reports USD and Google's billing account
//     here reports EUR. One money column implies one currency, and the only way
//     to make it true is a conversion rate this system would have to invent —
//     which is the thing Omar ruled out. Amounts are shown in what the provider
//     said, and subtotalled per currency, never added across.
//  2. A PROVIDER'S INVOICE IS COARSER THAN OUR CATEGORIES. Google's account
//     covers image generation AND translation, so `category` comes back `null`
//     and the charge belongs to no single row. Forcing it into one would be the
//     guess the gateway deliberately refuses to make.
//
// So the five categories keep saying what THIS SYSTEM recorded, and this section
// says what the providers billed, beside it and labelled.

// ⚠️ `PROVIDER_LABELS` LIVED HERE UNTIL 2026-09-16. It was a second list of the
//    same fact — which vendor bills which feature — maintained beside
//    `AI_COST_CATEGORIES` and agreeing with it only for as long as somebody
//    remembered to edit both (`feedback_one_rule_one_place`). The names now come
//    from `category.billedBy` in `@dynamo/schema`, where `costReporting` already
//    lived, and `ai-cost-categories.test.ts` asserts every key there is a
//    provider this system actually writes.

/**
 * What a provider's row can say, in the order the reasons are decided.
 *
 * ★ `broken` EXISTS BECAUSE `awaiting` IS A PROMISE. "Awaiting provider cost"
 *   tells the reader the figure is coming. When the credential has been revoked
 *   it is not coming, and saying so is the whole of requirement 4's second half.
 */
type ProviderCostState =
  | "reported" // the provider gave us amounts for this client and window
  | "broken" // configured, and the last import FAILED — a connection error
  | "unknown" // we could not read the import's own state, so we claim nothing
  | "awaiting" // working, and the provider has not published this window yet
  | "off" // the automatic import is switched off on purpose
  | "not_connected"; // no credential for this provider yet

function providerCostState(
  rows: readonly AiProviderCostRow[],
  status: CostImportStatus | null,
  provider: string,
): ProviderCostState {
  // An amount that has actually arrived outranks every explanation for why one
  // might not have. A figure on the screen is the strongest possible evidence
  // that the link worked at least once.
  if (rows.length > 0) return "reported";
  if (!status) return "unknown";
  const s = status.providers.find((p) => p.provider === provider);
  if (!s || !s.configured) return "not_connected";
  if (s.health === "failing") return "broken";
  if (!status.schedule.automatic) return "off";
  return "awaiting";
}

/**
 * Subtotals per currency. NEVER a single total — see the note above.
 *
 * ★ THREE FIGURES SINCE 2026-09-16 (Omar): the usage cost before credits, the
 *   credits, and the net payable. `amount` keeps its meaning — the net — so any
 *   existing reader is unchanged.
 *
 * ⚠️ `grossAmount` is ABSENT on a gateway without migration 0023, and the
 *    fallback is `amount`: with no credit information the best available reading
 *    of "what did this cost" is the net, and pretending otherwise would invent a
 *    discount. `credit` stays undefined in that case, so nothing on screen
 *    claims a credit was applied (`feedback_absence_and_emptiness_read_the_same`).
 */
export function totalsByCurrency(
  rows: readonly AiProviderCostRow[],
): ChargedAmount[] {
  const acc = new Map<
    string,
    { net: number; gross: number; credit: number | null }
  >();
  for (const r of rows) {
    const at = acc.get(r.currency) ?? { net: 0, gross: 0, credit: null };
    const credit = r.creditAmount ?? null;
    acc.set(r.currency, {
      net: at.net + r.amount,
      gross: at.gross + (r.grossAmount ?? r.amount),
      credit: credit === null ? at.credit : (at.credit ?? 0) + credit,
    });
  }
  return [...acc.entries()]
    .map(([currency, p]) => ({
      currency,
      amount: p.net,
      gross: p.gross,
      ...(p.credit === null ? {} : { credit: p.credit }),
    }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

/**
 * `$1.2345 USD`, or `1.23 EUR`.
 *
 * ★ THE CURRENCY CODE IS ALWAYS SHOWN, because the figure is never converted.
 *   Two providers reporting in two currencies sit on one screen, and the code is
 *   what stops a reader adding them.
 *
 * ★ AND IT KEEPS THE PROVIDER'S OWN PRECISION, up to six decimals — not the
 *   two-decimal rounding used for this tool's estimates. Omar, 2026-09-10: the
 *   amounts must "match Deepgram's report for the same period, currency and
 *   scope". A charge of $0.1234 rendered as $0.12 cannot be matched against
 *   anything; the screen would quietly disagree with the invoice it claims to be
 *   quoting, in the one place that has to be checkable
 *   (`feedback_a_check_can_match_the_prose_instead_of_the_value`). Six is the
 *   scale the column itself stores — `numeric(14,6)`.
 */
export function providerAmount(amount: number, currency: string): string {
  const decimals = (() => {
    // How many decimals the value actually has, clamped to what is meaningful.
    const fixed = amount.toFixed(6).replace(/0+$/, "");
    const after = fixed.split(".")[1] ?? "";
    return Math.min(6, Math.max(2, after.length));
  })();
  const n = amount.toFixed(decimals);
  return currency === "USD" ? `$${n} USD` : `${n} ${currency}`;
}

/**
 * The three times a reader needs before they can interpret "awaiting".
 *
 * ★ `nextCheck` is DERIVED from the last attempt plus the interval, and is
 *   omitted entirely when either is unknown rather than guessed from `now` — a
 *   "next check" that is really "now plus six hours" would be wrong by however
 *   long ago the last one ran, and wrong in the reassuring direction
 *   (`feedback_a_plausible_placeholder_is_worse_than_an_obvious_one`).
 */
/**
 * ⚠️ NO CALLER ON SCREEN SINCE 2026-09-16, and that is recorded rather than
 *    hidden. Omar simplified the schedule strip to one line, which removed the
 *    "Next check" item this feeds. It is kept — with its tests — because the
 *    calculation is correct, non-obvious (it must not project a time when
 *    automatic collection is off) and one line from being needed again if he
 *    asks for the item back. An un-called function that LOOKS live is the thing
 *    worth avoiding, so this says so (`feedback_annotate_only_after_proving_dead`).
 */
export function nextCheckAt(
  lastAttemptAt: string | null,
  intervalMinutes: number,
  automatic: boolean,
): Date | null {
  if (!automatic || !lastAttemptAt) return null;
  const last = new Date(lastAttemptAt);
  if (Number.isNaN(last.getTime())) return null;
  return new Date(last.getTime() + intervalMinutes * 60_000);
}

function CollectionSchedule({
  status,
  statusError,
}: {
  status: CostImportStatus | null;
  statusError: boolean;
}) {
  if (statusError || !status) return null;

  // The providers that can actually report per client. Google is configured to
  // report nothing per client, so folding it in would drag every figure here
  // toward "never checked".
  const providers = status.providers.filter((x) => x.configured);
  if (providers.length === 0) return null;

  const newest = (pick: (p: CostImportProviderStatus) => string | null) =>
    providers
      .map(pick)
      .filter((x): x is string => typeof x === "string")
      .sort()
      .at(-1) ?? null;

  const lastSuccess = newest((p) => p.lastSuccessAt);
  const item = (label: string, value: React.ReactNode) => (
    <div key={label} style={{ display: "grid", gap: 1 }}>
      <span
        style={{
          fontSize: 10.5,
          textTransform: "uppercase",
          letterSpacing: "0.04em",
          color: "var(--text-faint)",
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span style={{ fontSize: 12, color: "var(--text)" }}>{value}</span>
    </div>
  );

  return (
    <div
      style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 20,
        padding: "9px 11px",
        background: "var(--surface-inset)",
        borderRadius: "var(--radius-sm)",
      }}
    >
      {/* ── ★ ONE LINE, and this REVERSES an earlier instruction of his ─────
          Omar, 2026-09-16, with a mock-up: "I simplified the view, I've put only
          LAST SUCCESSFUL UPDATE 16 Sept 2026, 15:48."

          ⚠️ That supersedes his own 2026-09-11 instruction — "Include the last
             check, last successful update and next scheduled check" — which is
             why the two dropped items are recorded here rather than deleted
             quietly. "Next check" answered "how long must I wait?", and for
             Google that answer is hours, not minutes. He was told what the
             simplification costs and chose it; it is one line to restore.

          ⚠️ AND WHEN AUTOMATIC COLLECTION IS OFF, THIS STRIP STILL SAYS SO.
             A stale timestamp with no hint that nothing is coming is worse than
             no timestamp (`feedback_no_false_affordance_toggles`). That warning
             survived the simplification because it is not a schedule, it is a
             fault. */}
      {item(
        "Last successful update",
        lastSuccess ? (
          fmtDateTime(lastSuccess)
        ) : (
          <span style={{ color: "var(--warning-text)" }}>Never</span>
        ),
      )}
      {!status.schedule.automatic &&
        item(
          "Automatic collection",
          <span style={{ color: "var(--warning-text)" }}>
            Off — this figure will not move on its own
          </span>,
        )}
    </div>
  );
}

// ⚠️ `ProviderCostSection` LIVED HERE UNTIL 2026-09-16 and was deleted, not
//    orphaned. Omar: "I have only one Service Table with the Services split by
//    feature." Everything it uniquely carried moved rather than went:
//
//      · the provider's NAME        → each row's heading in `CategoryRowView`
//      · the per-currency TOTALS    → that one table's footer
//      · WHY a provider is silent   → `CategoryRow.providerState`, rendered by
//                                     `ChargeCell` through the same
//                                     `ProviderCostPlaceholder` it always used
//      · the OpenAI setup note      → rendered directly by `Report`
//
//    `ProviderCostRowView` went with it. `providerCostState`,
//    `ProviderCostPlaceholder` and `ProviderStatusLine` did NOT — they are the
//    moved-in parts, and they have callers.

/**
 * Why this company's OpenAI figure may be missing — shown ONLY when it is not
 * simply fine.
 *
 * ── Why this exists ────────────────────────────────────────────────────────
 *
 * OpenAI's invoice can only be split by project, so a company's OpenAI spend is
 * separable only if it has a project of its own. Until 2026-09-10 a company
 * without one silently used a shared key and its spend was reported as nobody's.
 * That fallback is gone — but the SCREEN still had no way to tell the two
 * innocent-looking zeroes apart: "set up, spent nothing" and "never set up, so
 * nothing could be attributed" both render as an empty row
 * (`feedback_absence_and_emptiness_read_the_same`).
 *
 * ★ SILENT WHEN `ready`. A banner that is always present stops being read, and
 *   "this is working" is not news. The row above already carries the figure.
 */
type CreateProjectResult = {
  state: string;
  detail: string | null;
  created: boolean;
  createdServiceAccount: boolean;
};

function OpenAiSetupNote({
  setup,
  onCreate,
}: {
  setup: AiCostReport["openAiSetup"];
  // Absent when this Console is talking to a gateway that predates the route —
  // the note then renders exactly as it did before, without a dead button
  // (`feedback_two_hosts_one_merge_means_a_new_api_field_must_be_optional`).
  onCreate?: (() => Promise<CreateProjectResult>) | undefined;
}) {
  if (!setup || setup.state === "ready") return null;

  // Each state gets its own sentence. A single generic one would be the clipped
  // message this project has been bitten by before.
  const said: Record<string, { title: string; body: string }> = {
    missing: {
      title: "This company does not have its own OpenAI project yet",
      // ⚠️ THIS USED TO SAY it is created "again on its first question
      //    generation if that did not happen". MEASURED FALSE on 2026-09-13:
      //    that path runs under a role deliberately denied the table, and no
      //    project has EVER been created by it in any environment. Leaving the
      //    sentence would have told Omar to wait for something that never comes
      //    (`feedback_a_comment_is_not_a_contract`, applied to on-screen words).
      body:
        "It is created when the company is approved. This company did not arrive that way — " +
        "so it needs one creating here. Until then no OpenAI charge can be attributed to it: " +
        "an empty OpenAI row above means \u201cnot set up\u201d, not \u201cnothing spent\u201d.",
    },
    provisioning: {
      title: "This company's OpenAI project is being set up",
      // Same correction as above: nothing resumes on its own, because the
      // request path cannot write the table it would have to write.
      body:
        "Setup started and has not finished. It does not resume on its own — use the button " +
        "below, which is safe to press: it adopts whatever already exists rather than making a second one.",
    },
    incomplete: {
      title: "This company's OpenAI setup did not finish",
      body:
        setup.detail ?? "Setup stopped part-way and no reason was recorded.",
    },
  };
  const note = said[setup.state] ?? {
    title: "This company's OpenAI setup is in an unrecognised state",
    // ★ Names the value rather than hiding it. An unknown state is a fact about
    //   the system, and inventing a friendly sentence for it would be a
    //   plausible placeholder (`feedback_a_plausible_placeholder_is_worse_than_an_obvious_one`).
    body: `The recorded state is \u201c${setup.state}\u201d, which this screen does not have wording for.`,
  };

  return (
    <div
      // ★ A STABLE MARKER, and it is load-bearing for the test rather than
      //   decorative. Asserting the note's ABSENCE by searching for its wording
      //   is unreliable: every state has different wording, so a note that
      //   wrongly renders for a `ready` company falls into the
      //   unrecognised-state branch and matches none of the phrases a test
      //   would look for. The test passed with the bug present — a control that
      //   cannot fail (`feedback_a_false_negative_is_a_broken_instrument_too`).
      data-openai-setup-note={setup.state}
      style={{
        border: "1px solid var(--border)",
        // The design system's own token. A hex fallback here is a lint
        // failure by design: `design-system.test.ts` refuses any colour literal
        // outside the generated stylesheet, so the palette has ONE home.
        borderLeft: "3px solid var(--warning)",
        borderRadius: 6,
        padding: "10px 12px",
        display: "grid",
        gap: 3,
      }}
    >
      <div
        style={{ fontSize: 12.5, fontWeight: 600, color: "var(--text-strong)" }}
      >
        {note.title}
      </div>
      <div
        style={{ fontSize: 11.5, color: "var(--text-muted)", lineHeight: 1.5 }}
      >
        {note.body}
      </div>
      {onCreate && <CreateOpenAiProject onCreate={onCreate} />}
    </div>
  );
}

/**
 * What the provisioning attempt came to — rendered ABOVE the report, so it
 * survives the re-read that success triggers.
 *
 * ★ IT DISTINGUISHES "created" FROM "adopted". The project name is a pure
 *   function of the company, so the same company derives the same name in EVERY
 *   environment — and row ids were copied identically to staging and production.
 *   Pressing the button on staging for a company that already has a QA project
 *   therefore JOINS them, and the two environments' spend stops being separable.
 *   A bare "Done" would hide that completely
 *   (`feedback_identical_seeds_make_one_identity_a_cross_environment_key`).
 */
function ProvisionResultNote({
  result,
}: {
  result: CreateProjectResult | null;
}) {
  if (!result) return null;
  const ok = result.state === "ready";
  return (
    <div
      data-openai-provision-result={result.state}
      style={{
        border: "1px solid var(--border)",
        borderLeft: `3px solid var(--${ok ? "success" : "warning"})`,
        borderRadius: 6,
        padding: "10px 12px",
        marginBottom: 12,
        fontSize: 11.5,
        lineHeight: 1.55,
        color: "var(--text-muted)",
      }}
    >
      {ok
        ? result.created
          ? "Done — a new OpenAI project was created for this company."
          : "Done — an OpenAI project with this company's name already existed, so it was re-used rather than duplicated. If that project belongs to another environment, the two will now share one bill."
        : `Not finished: ${result.detail ?? "no reason was recorded."}`}
    </div>
  );
}

/**
 * The button, and what it says afterwards.
 *
 * ★ IT DISTINGUISHES "created" FROM "adopted", and that is the whole reason it
 *   reports anything beyond success. The project name is a pure function of the
 *   company, so the same company derives the same name in EVERY environment —
 *   and row ids were copied identically to staging and production. Pressing this
 *   on staging for a company that already has a QA project therefore joins them,
 *   and the two environments' spend stops being separable. A bare "Done" would
 *   hide that completely
 *   (`feedback_identical_seeds_make_one_identity_a_cross_environment_key`).
 *
 * ★ The button LATCHES while it runs. Provisioning creates something at a third
 *   party that cannot be rolled back, so a second click is not a retry — it is a
 *   second attempt at something already in flight
 *   (`feedback_a_disabled_prop_is_not_a_latch`,
 *   `feedback_an_eventually_consistent_api_cannot_be_rolled_back`).
 */
function CreateOpenAiProject({
  onCreate,
}: {
  onCreate: () => Promise<CreateProjectResult>;
}) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const inFlight = React.useRef(false);

  return (
    <div style={{ marginTop: 6, display: "grid", gap: 4 }}>
      <div>
        <button
          type="button"
          className="btn"
          disabled={busy}
          onClick={async () => {
            // The ref, not the state, is the latch: two clicks in the same tick
            // both see `busy === false`.
            if (inFlight.current) return;
            inFlight.current = true;
            setBusy(true);
            setErr(null);
            try {
              await onCreate();
            } catch (e) {
              setErr(e instanceof Error ? e.message : String(e));
            } finally {
              inFlight.current = false;
              setBusy(false);
            }
          }}
        >
          {busy ? "Creating…" : "Create this company's OpenAI project"}
        </button>
      </div>
      {err && (
        <div style={{ fontSize: 11.5, color: "var(--error-text)" }}>
          It could not be created: {err}
        </div>
      )}
    </div>
  );
}

function ProviderCostPlaceholder({ state }: { state: ProviderCostState }) {
  const style = { fontSize: 12.5, fontWeight: 500 as const };
  if (state === "broken") {
    return (
      <span style={{ ...style, color: "var(--error-text)" }}>
        Cannot read billing
      </span>
    );
  }
  if (state === "not_connected") {
    return (
      <span style={{ ...style, color: "var(--text-faint)" }}>
        Not connected
      </span>
    );
  }
  if (state === "off") {
    return (
      <span style={{ ...style, color: "var(--warning-text)" }}>
        Import switched off
      </span>
    );
  }
  if (state === "unknown") {
    return (
      <span style={{ ...style, color: "var(--warning-text)" }}>
        Status unavailable
      </span>
    );
  }
  return (
    <span style={{ ...style, color: "var(--warning-text)" }}>
      Awaiting provider cost
    </span>
  );
}

/**
 * One line under a provider naming what is happening and what to do.
 *
 * ⚠️ For a failure it repeats THE PROVIDER'S OWN WORDS. A 403 from Deepgram says
 *    which scope is missing, by name; paraphrasing it sends whoever reads this
 *    to the wrong switch in a dashboard they do not know
 *    (`feedback_a_clipped_message_turns_an_answer_into_a_fault`).
 */
function ProviderStatusLine({
  state,
  status,
  statusError,
}: {
  state: ProviderCostState;
  status: CostImportProviderStatus | null;
  statusError: boolean;
}) {
  const base = { fontSize: 11.5, marginTop: 4, lineHeight: 1.5 };

  if (state === "unknown") {
    return (
      <div style={{ ...base, color: "var(--warning-text)" }}>
        {statusError
          ? "The import's own status could not be read, so nothing is claimed about whether it is working."
          : "Checking the import's status."}
      </div>
    );
  }

  if (state === "not_connected") {
    return (
      // ⚠️ Shortened 2026-09-14 (Omar). It repeated verbatim under EVERY
      // unconnected provider — three identical sentences on one screen — and the
      // second half ("Dynamo staff set this up; authors are never asked for it")
      // reassured the only audience this console has: Dynamo staff.
      <div style={{ ...base, color: "var(--text-muted)" }}>
        No billing credential yet, so nothing can be imported.
      </div>
    );
  }

  if (state === "broken") {
    return (
      <div style={{ ...base, color: "var(--error-text)" }}>
        <strong>Billing cannot be read.</strong>{" "}
        {status?.lastAttemptMessage ??
          "The last import failed and gave no reason."}
        {status?.lastSuccessAt ? (
          <>
            {" "}
            Last successful update {fmtDateTime(status.lastSuccessAt)}, and
            nothing since.
          </>
        ) : (
          <>
            {" "}
            It has never succeeded, so no charge from this provider has ever
            been imported.
          </>
        )}
      </div>
    );
  }

  if (state === "off") {
    return (
      <div style={{ ...base, color: "var(--warning-text)" }}>
        The automatic import is switched off in this environment, so no new
        charges are arriving.
      </div>
    );
  }

  if (state === "awaiting") {
    return (
      <div style={{ ...base, color: "var(--text-muted)" }}>
        {status?.health === "never_run"
          ? "Connected, and no import has run yet."
          : status?.health === "stale"
            ? `Connected, but the last successful update was ${
                status.lastSuccessAt
                  ? fmtDateTime(status.lastSuccessAt)
                  : "some time ago"
              } — longer ago than expected.`
            : "Connected and working. The provider has not published a figure for this period yet."}
      </div>
    );
  }

  // Reported.
  //
  // ⚠️ SILENT WHEN IT IS SIMPLY WORKING (Omar, 2026-09-14, naming the sentence:
  //    "Last updated 14 Sept 2026, 16:59, covering up to 15 Sept 2026."). It
  //    repeated under every healthy provider, and the same two facts are in the
  //    schedule strip at the top of this section — once, for all of them.
  //
  // ★ But NOT silent when the figure is stale. `health === "failing"` means a
  //   number is on screen AND the most recent attempt to refresh it failed,
  //   which the strip above cannot say per provider. That case keeps its
  //   sentence, and keeps the timestamp with it — "may be behind" is useless
  //   without saying behind WHAT (`feedback_absence_and_emptiness_read_the_same`).
  if (status?.health !== "failing") return null;
  return (
    <div style={{ ...base, color: "var(--warning-text)" }}>
      {status?.lastSuccessAt
        ? `Last updated ${fmtDateTime(status.lastSuccessAt)}`
        : "Imported from the provider"}
      {status?.lastSuccessWindowEnd
        ? `, covering up to ${fmtDate(status.lastSuccessWindowEnd)}`
        : ""}
      {" — but the most recent attempt failed, so this figure may be behind."}
    </div>
  );
}

/**
 * Charges a provider made that belong to no client.
 *
 * ★ Omar, 2026-09-08: "Document any attribution gaps explicitly." A charge
 *   nobody can be billed for has, by definition, no customer page of its own —
 *   and inventing an apportionment for it is the thing this project has refused
 *   since migration 0012. So it appears here, on every company page, LABELLED AS
 *   PLATFORM-WIDE and collapsed.
 *
 * ⚠️ THE LABELLING IS THE WHOLE DESIGN. An unattributed total shown without that
 *    word, on a page headed by one company's name, reads as that company's
 *    charge — which is the misreading the gateway refuses to make and the screen
 *    must not reintroduce (`feedback_a_readable_label_can_name_the_wrong_thing`).
 *    It is also NOT the standalone AI-spend screen Omar removed on 2026-09-08:
 *    it adds no per-client figure and no manual entry, and answers exactly one
 *    question — how much of the bill could not be placed.
 *
 * ★ AND IT LOADS ONLY WHEN OPENED. A third request on every company page, for a
 *   figure that is the same on all of them, would be paid for by everyone to be
 *   read by almost nobody.
 */
function AttributionGaps({ api, r }: { api: ConsoleApi; r: AiCostReport }) {
  const [open, setOpen] = React.useState(false);
  const window = React.useMemo(() => dateWindowOf(r.period), [r.period]);
  const { state, retry } = useAsync<AiAttributionGaps | null>(
    () =>
      open
        ? api.aiCostAttributionGaps(window.start, window.end)
        : Promise.resolve(null),
    [open, window.start, window.end],
  );

  return (
    // ★ A CONTROLLED `<details>`: React owns `open`, and the summary's default
    //   toggle is prevented so the two cannot disagree.
    //
    //   The sibling "Where these figures come from" block is an ordinary
    //   uncontrolled `<details>`, which is right for it — it only reveals text.
    //   This one starts a REQUEST when it opens, so the moment it opens has to be
    //   something the component knows rather than something it observes. Relying
    //   on the native `toggle` event also made this untestable outside a real
    //   browser (`feedback_a_harness_limitation_can_mimic_the_defect`), and the
    //   same shape is used for the same reason wherever a disclosure has a side
    //   effect.
    <details open={open}>
      <summary
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          cursor: "default",
          padding: "4px 0",
        }}
        onClick={(e) => {
          e.preventDefault();
          setOpen((o) => !o);
        }}
      >
        Charges that could not be matched to any client
      </summary>
      <div
        style={{
          fontSize: 11.5,
          color: "var(--text-muted)",
          lineHeight: 1.6,
          padding: "8px 0 0",
        }}
      >
        <p style={{ margin: "0 0 6px" }}>
          Across <strong>all</strong> companies, not this one. A charge appears
          here when the provider's report carried no client marker, or carried
          one this tool does not recognise — usually work done before client
          tagging was switched on, or a request made by hand.
        </p>
        {state.t === "loading" && (
          <Loading label="Reading unmatched charges…" />
        )}
        {state.t === "err" && <ErrorState err={state.err} onRetry={retry} />}
        {state.t === "ok" && state.data !== null && (
          <GapList gaps={state.data} />
        )}
      </div>
    </details>
  );
}

/**
 * What one unmatched charge IS — the sentence shown in place of a marker.
 *
 * ★ Omar, 2026-09-11: "Keep BigQuery costs separate as Dynamo infrastructure,
 *   excluded from client charges. Keep unknown or unattributed charges
 *   visible." Both halves matter: nothing is hidden, and the three kinds are
 *   not made to look alike.
 *
 * Exported so the tests drive the real function rather than a copy of its rules
 * (`feedback_a_test_double_that_reimplements_drifts`).
 */
export function gapExplanation(g: AiAttributionGap): {
  text: string;
  /** `true` → counts toward "could not be matched to a client". */
  isFault: boolean;
  tone: "fault" | "explained";
} {
  // A charge we can NAME is not an attribution failure — the internal-testing
  // case, which predates the Google work and still applies.
  const internal = internalAttributionLabel(g.attributionKey);
  if (internal) return { text: internal, isFault: false, tone: "explained" };

  const c = g.classification;
  if (c && c.kind === "infrastructure") {
    return {
      // Not "could not be matched". There is nothing to match it to.
      text: c.reason
        ? `Dynamo's own running costs — ${c.reason.replace(/\.$/, "")}`
        : "Dynamo's own running costs, belonging to no client",
      isFault: false,
      tone: "explained",
    };
  }
  if (c && c.kind === "unknown") {
    return {
      text: c.reason
        ? `Not recognised yet — ${c.reason.replace(/\.$/, "")}`
        : "A charge this tool does not recognise yet",
      isFault: false,
      tone: "explained",
    };
  }
  // ★ The genuine gap: work done for a client that the provider could not tie
  //   to one. The marker EXACTLY as the provider reported it, unparsed — it is
  //   the only evidence of what the provider actually said, and it is what
  //   makes the gap diagnosable rather than merely counted.
  return {
    text:
      g.attributionKey === null
        ? "no marker was sent"
        : `marker \u201C${g.attributionKey}\u201D`,
    isFault: true,
    tone: "fault",
  };
}

function GapList({ gaps }: { gaps: AiAttributionGaps }) {
  if (gaps.gaps.length === 0) {
    return (
      <div>
        Every charge imported for {fmtDate(gaps.window.start)} to{" "}
        {fmtDate(gaps.window.end)} was matched to a client.
      </div>
    );
  }
  return (
    <div style={{ display: "grid", gap: 4 }}>
      {gaps.totalsByCurrency.map((t) => {
        // ★ THREE TOTALS, NOT ONE. The old version split only Dynamo's internal
        //   testing out of the unmatched total; since Google's invoice arrived
        //   it also carries Dynamo's own INFRASTRUCTURE and charges no rule
        //   recognises yet. Summing all three as "could not be matched" would
        //   report a growing attribution problem that does not exist.
        const mine = gaps.gaps.filter((g) => g.currency === t.currency);
        const explained = mine
          .filter((g) => !gapExplanation(g).isFault)
          .reduce((n, g) => n + g.amount, 0);
        const unexplained = t.amount - explained;
        const internalAmount = mine
          .filter((g) => internalAttributionLabel(g.attributionKey))
          .reduce((n, g) => n + g.amount, 0);
        const infra = mine
          .filter((g) => g.classification?.kind === "infrastructure")
          .reduce((n, g) => n + g.amount, 0);
        const unrecognised = mine
          .filter((g) => g.classification?.kind === "unknown")
          .reduce((n, g) => n + g.amount, 0);
        return (
          <div key={t.currency} style={{ display: "grid", gap: 2 }}>
            {unexplained > 0 && (
              <span style={{ color: "var(--warning-text)", fontWeight: 500 }}>
                {providerAmount(unexplained, t.currency)} could not be matched
                to a client.
              </span>
            )}
            {internalAmount > 0 && (
              <span style={{ color: "var(--text-muted)" }}>
                {providerAmount(internalAmount, t.currency)} is Dynamo's own
                testing, named below and still counted in the reconciliation.
              </span>
            )}
            {infra > 0 && (
              <span style={{ color: "var(--text-muted)" }}>
                {providerAmount(infra, t.currency)} is Dynamo's own running
                costs — never a client charge, and still counted in the
                reconciliation.
              </span>
            )}
            {unrecognised > 0 && (
              <span style={{ color: "var(--text-muted)" }}>
                {providerAmount(unrecognised, t.currency)} is not recognised yet
                and is listed below so it can be identified.
              </span>
            )}
          </div>
        );
      })}
      {gaps.gaps.map((g, i) => {
        const e = gapExplanation(g);
        return (
          <div key={`${g.provider}-${g.attributionKey ?? "none"}-${i}`}>
            {g.provider} · {providerAmount(g.amount, g.currency)} ·{" "}
            <span
              style={{ color: e.tone === "fault" ? undefined : "var(--text)" }}
            >
              {e.text}
            </span>
            {/* Google's own words, so a reader can see what the sentence above
                was derived from rather than having to trust it. */}
            {g.service ? ` · ${g.service}` : ""}
            {g.lineItem ? ` · ${g.lineItem}` : ""}
            {g.classification?.provisional ? (
              <span style={{ color: "var(--text-muted)" }}>
                {" "}
                · classified provisionally — this kind of charge has not been
                seen here before
              </span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

/**
 * The report's ISO instants as the half-open DATE window every provider reports in.
 *
 * ⚠️ The end is nudged forward when truncation would collapse the window. A
 *    half-open window whose bounds are equal is EMPTY, and the route refuses it —
 *    so a period of "today" would answer 400 rather than "nothing yet", which
 *    reads on screen as a broken panel rather than an empty one.
 */
export function dateWindowOf(period: { from: string; to: string }): {
  start: string;
  end: string;
} {
  const start = period.from.slice(0, 10);
  let end = period.to.slice(0, 10);
  if (end <= start) {
    end = new Date(new Date(`${start}T00:00:00Z`).getTime() + 86_400_000)
      .toISOString()
      .slice(0, 10);
  }
  return { start, end };
}

// ── Provenance, collapsed ───────────────────────────────────────────────────

function Provenance({
  r,
  status,
}: {
  r: AiCostReport;
  status: CostImportStatus | null;
}) {
  return (
    <details>
      <summary
        style={{
          fontSize: 12,
          color: "var(--text-muted)",
          cursor: "default",
          padding: "4px 0",
        }}
      >
        Where these figures come from
      </summary>
      <div
        style={{
          fontSize: 11.5,
          color: "var(--text-muted)",
          lineHeight: 1.6,
          padding: "8px 0 0",
          display: "grid",
          gap: 4,
        }}
      >
        <div>
          The Authoring Tool records one entry for every AI request it makes,
          including the calls it paid for and threw away.{" "}
          {MONEY_WORDS.provenance}
        </div>
        <div>
          Most recent request recorded:{" "}
          {r.lastRecordedAt
            ? fmtDateTime(r.lastRecordedAt)
            : "none in this period"}
          .
        </div>
        {r.models.length > 0 && <div>Models used: {r.models.join(", ")}.</div>}
        {r.byEnvironment.length > 0 && (
          <div>
            Development and test usage is separated here:{" "}
            {r.byEnvironment
              .map((e) => `${e.label} ${count(e.requests)}`)
              .join(" · ")}
            .
            {r.isInternal
              ? " This workspace is marked as an internal one."
              : ""}
          </div>
        )}
        {/* ⚠️ CORRECTED 2026-09-10. This line used to read "Google's own billing
            figures are not connected yet, so no comparison is shown" — true of
            Google and, by omission, wrong about everything else: Deepgram's real
            charges DO arrive now. What is connected is stated per provider in
            the section above; this says how often it is checked. */}
        {status ? (
          <div>
            {status.schedule.automatic
              ? `Charges are imported from the providers automatically, about every ${
                  status.schedule.intervalMinutes >= 60
                    ? `${Math.round(status.schedule.intervalMinutes / 60)} hours`
                    : `${status.schedule.intervalMinutes} minutes`
                }, re-reading the last ${status.schedule.lookbackDays} days so a corrected figure replaces the old one.`
              : "The automatic import of provider charges is switched off in this environment."}
          </div>
        ) : (
          <div>How often provider charges are imported could not be read.</div>
        )}
        {r.google.sharedProjectId ? (
          <a
            className="btn ghost sm focusable"
            href={r.google.billingUrl ?? "#"}
            target="_blank"
            rel="noreferrer"
            style={{
              marginTop: 6,
              display: "inline-flex",
              justifySelf: "start",
            }}
          >
            Open Google Billing — shared project “{r.google.sharedProjectId}”
          </a>
        ) : (
          // No guessed URL. A link to the wrong project's billing looks
          // authoritative and is not.
          <div>
            No Google Cloud project is configured for this environment, so there
            is no billing link to open.
          </div>
        )}
      </div>
    </details>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ⚠️ THE "COST COMPARISON" PANEL WAS REMOVED HERE — Omar, 2026-09-15:
//   "Completely remove the Cost comparison area, it is now way too complicated
//    to understand so remove the content and any piece of code related to that
//    section."
//
// ★ THIS REVERSES OQ-113, which he answered on 2026-09-12 asking for exactly
//   this panel ("Preserve the Dynamo calculation so I can compare the values and
//   understand any difference"). It is his decision to reverse, recorded in
//   17_OPEN_QUESTIONS.md rather than quietly overwritten. What he saw in
//   practice was one row carrying four stacked explanations — a credit line, a
//   partial-coverage line, a signed difference, and four reasons for it —
//   which is what "way too complicated" refers to.
//
// ★★ THE DATA IS PARKED, NOT DELETED, so restoring this costs a UI, not a
//    re-derivation (`feedback_park_a_disabled_slice_dont_omit_it`):
//      · `ai_usage` still stores `cost_components`, `cost_pricing` and
//        `indicative_state` for every operation — Dynamo's own calculation
//        keeps being RECORDED, it is simply no longer displayed;
//      · `ai-cost-indicative.ts` and `google-prices.ts` are untouched and still
//        run at write time, via `ai-usage.ts`;
//      · `ai_provider_cost` still stores Google's own gross, credit and period.
//    What went is the COMPARISON: `ai-cost-columns.ts`, `ai-cost-reconcile.ts`
//    and this panel, which existed only to put the two side by side.
//
// Removed with this block: CostColumnsPanel, ComparisonRow, SERVICE_LABEL,
// REPORTED_LABEL, columnMoney, colTh, colTd, colSub.
// See wiki/findings/2026-09-15-the-cost-comparison-panel-removed.md
